import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box as MuiBox, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";

import { CodeNexusChat, Composer } from "@codenexus/react-mui";
import * as CodeNexusMui from "@codenexus/react-mui";
import type { HookDefinition, PreToolUseInput, UserPromptSubmitInput } from "@codenexus/core";
import {
    createMemoryStore,
    useChatSession,
    useAgentEvents,
    useUIEventBusContext,
    UIEventBusProvider,
    UserStateProvider,
    PageOrchestrator as CodeNexusPageOrchestrator,
    createClientStateApi,
    createNavigationBridge,
    createFileIntakeBridge,
    createFileRouter,
    createContextMeshBridge,
    type NavigationAdapter,
    type FileIntakeAdapter,
    type UIEventBus,
    type PageRuleMap,
    type UseHostCards
} from "@codenexus/react";
import "@codenexus/react-mui/styles.css";
import { createResilientClientStateApi } from "./clientStateApi";

import {
    createChatTools,
    type CurrentHierarchyContext,
    type CurrentProductContext,
    type CurrentWorkProductContext
} from "./chatTools/createChatTools";
import { createDisconnectStateStore } from "./chatTools/integrationDisconnect/integrationDisconnect";
import type { AppChatEvents, AppChatEventMap, AppChatEventName, RequestWorkProductGeneration } from "./chatTools/types";
import {
    ExposeHostCards,
    IngestionBatchRegistryContext,
    KnowledgeIngestionHostCard,
    KNOWLEDGE_INGESTION_CARD,
    createIngestionBatchRegistry
} from "./chatComponents/ingestionHostCard";
import { ACCEPTED_FILE_TYPES } from "./chatComponents/IngestionCard";
import { getHostDocumentBridge, DocumentContextWatcher, createSwitchDocumentTool, StagedEditChips } from "@/components/aiBridge";
import { InteligenicUIAdapter } from "@/components/aiBridge/InteligenicUIAdapter";
import { getHeadlessSectionEditors } from "@/components/aiBridge/HeadlessSectionEditors";
import { createUIBridge, type UIBridgeBundle } from "@codenexus/react";
import { setActiveTab as setActiveTabRedux, selectActiveTabSectionId } from "@/state/activeEditor/activeEditorSlice";
import { selectAllSectionsArray } from "@/state/documentContext/documentContextSlice";
import { removeVectorFile } from "@/state/vectorFiles/vectorFilesSlice";
import { inferEditorModeFromWorkProductContext } from "./inferEditorMode";
import type { NewProductDraftSnapshot } from "./newProduct/state";
import type { NewProductFormHandle } from "./newProduct/NewProductScreen";
import {
    buildViewContextPayload,
    GLOBAL_HELP_SKILL,
    HELP_SLASH_COMMANDS,
    deriveCurrentModule,
    isContextMeshListPrompt,
    isScoreDetailBackToContextMeshPrompt,
    activateScoreDetailBackControl,
    isDirectContextMeshCreationPrompt,
    isDetailedContextMeshCreationPrompt,
    isDirectContextMeshHubPrompt,
    isDirectFilesPagePrompt,
    resolveDirectHierarchySelectionPrompt,
    resolveDirectContextMeshOpenPrompt,
    resolveDirectSddStagePrompt,
    planSectionNavigation,
    routeWithSearch,
    resolveTaxonomyForChat,
    shouldFireFeatureStudioWelcome,
    type AmbientProductContext
} from "./AIChatPanelHost";
import {
    BatchGenerationBridge,
    createBatchGenerationTools,
    isBatchGenerationPrompt,
    useBatchGeneration
} from "./batchGeneration";
import { createIntelligenicContextMeshAdapter, type FeatureStudioEntityOpenResult } from "./meshStrength/contextMeshBridgeAdapter";
import { resolveWorkProductGenerationName } from "./feature-studio/sddWorkflow";
import { getIntelligenicToken } from "@/auth/getIntelligenicToken";
import { listContextMeshes, resolveContextMeshByName } from "./contextMesh/contextMeshApi";

type BridgeRendererTools = Parameters<(typeof CodeNexusMui)["withUIRenderers"]>[0];

const withUIRenderers =
    ((CodeNexusMui as { withUIRenderers?: (tools: BridgeRendererTools) => BridgeRendererTools }).withUIRenderers ??
        ((tools: BridgeRendererTools) => tools));

const withContextMeshRenderers =
    ((CodeNexusMui as { withContextMeshRenderers?: (tools: BridgeRendererTools) => BridgeRendererTools }).withContextMeshRenderers ??
        ((tools: BridgeRendererTools) => tools));

const AGENT_URL = import.meta.env.VITE_AGENT_URL || "http://localhost:8000";
const INTEGRATION_AGENT_URL = import.meta.env.VITE_INTEGRATION_AGENT_URL || AGENT_URL;
const INTEGRATION_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

const FILE_DELETION_CONFIRMATION_HOOK: HookDefinition = {
    event: "PreToolUse",
    matcher: "delete_uploaded_file",
    handler: () => ({ decision: "ask" as const, reason: "Confirm permanent deletion of the resolved uploaded file." })
};

const DIRECT_SDD_STAGE_NAVIGATION_HOOK: HookDefinition = {
    event: "UserPromptSubmit",
    name: "route-sdd-stage-directly",
    handler: (input: UserPromptSubmitInput) => {
        if (input.event !== "UserPromptSubmit") return;
        const stage = resolveDirectSddStagePrompt(input.prompt);
        if (!stage) return;
        return {
            additionalContext: `MANDATORY DIRECT ROUTE: Call navigate_to_sdd_stage({ stage: "${stage}" }) immediately. Do not search for or activate a skill. Do not call navigate_to_files, list_ui_elements, or activate_ui_element. End the turn after the stage navigation succeeds.`,
            skipIntentRouting: true,
            clearActiveIntent: true
        };
    }
};

const DIRECT_CONTEXT_MESH_CREATION_HOOK: HookDefinition = {
    event: "UserPromptSubmit",
    name: "route-context-mesh-creation-directly",
    handler: (input: UserPromptSubmitInput) => {
        if (input.event !== "UserPromptSubmit") return;
        const isShortCommand = isDirectContextMeshCreationPrompt(input.prompt);
        const isDetailedRequest = isDetailedContextMeshCreationPrompt(input.prompt);
        if (!isShortCommand && !isDetailedRequest) return;
        return {
            additionalContext: isDetailedRequest
                ? "HOST NAVIGATION COMPLETED: The application is opening Create a Context Mesh for this detailed request. Preserve every supplied value. Do not activate a skill, create a todo list, inspect UI elements, or navigate again. Call get_context_mesh_draft(), propose the exact values, and ask for confirmation."
                : "MANDATORY DIRECT ROUTE: Call navigate_to_new_context_mesh() immediately. Do not search for or activate a skill. End the turn after navigation succeeds.",
            skipIntentRouting: true,
            clearActiveIntent: true
        };
    }
};

const DIRECT_CONTEXT_MESH_HUB_HOOK: HookDefinition = {
    event: "UserPromptSubmit",
    name: "route-context-mesh-hub-directly",
    handler: (input: UserPromptSubmitInput) => {
        if (input.event !== "UserPromptSubmit" || !isDirectContextMeshHubPrompt(input.prompt)) return;
        return {
            additionalContext: "HOST NAVIGATION COMPLETED: The application opened Context Mesh Hub. Respond only: \"Opening Context Mesh Hub.\" Do not inspect UI controls or activate a skill.",
            skipIntentRouting: true,
            clearActiveIntent: true
        };
    }
};

const SCORE_DETAIL_BACK_TO_CONTEXT_MESH_HOOK: HookDefinition = {
    event: "UserPromptSubmit",
    name: "return-from-score-detail-report",
    handler: (input: UserPromptSubmitInput) => {
        if (input.event !== "UserPromptSubmit" || !isScoreDetailBackToContextMeshPrompt(input.prompt)) return;
        if (typeof document === "undefined" || !activateScoreDetailBackControl(document)) {
            return {
                additionalContext:
                    "SCORE REPORT RETURN FAILED: The Back to Context Mesh control is not available on the current page. Say that plainly and do not list Context Meshes or navigate to Context Mesh Hub.",
                skipIntentRouting: true,
                clearActiveIntent: true
            };
        }
        return {
            additionalContext:
                "HOST NAVIGATION COMPLETED: The Score Detail Report was closed and the application returned to the same individual Context Mesh. " +
                "Respond only: \"Returned to the Context Mesh.\" Do not list Context Meshes, navigate to Context Mesh Hub, or call any tools.",
            skipIntentRouting: true,
            clearActiveIntent: true
        };
    }
};

const BLOCK_REDUNDANT_UPLOAD_SKILL_HOOK: HookDefinition = {
    event: "PreToolUse",
    matcher: "Skill",
    name: "block-redundant-upload-navigation-skill",
    handler: (input: PreToolUseInput) => {
        if (input.event !== "PreToolUse" || input.call.name !== "Skill") return;
        const skillName = String(input.call.arguments?.name ?? "").trim().toLowerCase();
        if (skillName !== "navigate-to-uploads") return;
        return {
            decision: "deny" as const,
            reason:
                "Do not load navigate-to-uploads. Call the registered navigate_to_files host tool immediately; it requires no confirmation."
        };
    }
};

const BLOCK_FILES_UI_ACTIVATION_HOOK: HookDefinition = {
    event: "PreToolUse",
    matcher: "activate_ui_element",
    name: "block-files-ui-navigation",
    handler: (input: PreToolUseInput) => {
        if (input.event !== "PreToolUse" || input.call.name !== "activate_ui_element") return;
        if (String(input.call.arguments?.elementId ?? "") !== "nav.uploads") return;
        return {
            decision: "deny" as const,
            reason: "The Files page is already being opened directly. Do not request approval or activate nav.uploads; call navigate_to_files if confirmation is needed."
        };
    }
};

const PROJECT_SETUP_SYSTEM_PROMPT = [
    "You are the product-setup assistant for Intelligenic.",
    "",
    "LANGUAGE — MANDATORY:",
    "Use English for every assistant response and every free-text tool argument, including rationale, explanation, and description fields. Keep registered tool names, element IDs, code identifiers, and user-provided proper nouns unchanged; never translate or invent them.",
    "",
    "CRITICAL TERMINOLOGY — USER-FACING LANGUAGE ONLY:",
    "In Intelligenic, the top-level entity is called a 'product'. In all responses to the user, ALWAYS say 'product' — never 'project'. Do NOT say 'project and product are the same thing' or treat them as interchangeable in your replies.",
    "If a user uses the word 'project' (e.g. 'how many projects do I have?'), gently correct them — e.g. 'Just to clarify, in Intelligenic these are called products — you have X product(s).' Always fold the correction naturally into your answer rather than making it a separate message.",
    "Note: some internal tool names use the word 'project' (e.g. list_projects, navigate_to_project). You MUST still call these tools as needed — this rule only applies to the language you use when speaking to the user, not to which tools you call.",
    "",
    "Default to product setup ONLY when no document is open in the editor. Treat a document as open/editable only when open_documents contains an entry with active: true. If open_documents only lists entries with active: false, do NOT assume document-editing mode.",
    "",
    "When in product-setup mode: users come here to create a new product from scratch. Workflows like 'add a feature' or 'create a user story' are not supported yet.",
    "Opening/selecting an existing feature or user story IS supported via navigation tools.",
    "",
    "Never reference filesystem operations, slash commands, or codebase exploration.",
    "",
    "## NEW PRODUCT CREATION — CRITICAL",
    "",
    "When the user wants to create a new product and is not already on the standalone form, follow the build-new-product skill and call navigate_to_new_product().",
    "When currentPage is 'new-product', do NOT repeat the discovery questionnaire in chat. Call get_new_product_draft() before giving advice, use the visible form as the source of truth, and help the user strengthen missing or weak fields. Calling get_new_product_draft establishes a new-task boundary: after it returns, respond only about the current new-product draft or the user's current request. Do not mention, summarize, continue, or offer follow-up for any action from before the user opened the new-product screen, including integration connection or disconnection actions, even when those actions remain in chat history or context notes.",
    "When currentPage is 'new-context-mesh', call get_context_mesh_draft() first, treat the visible Create Context Mesh form as the source of truth, propose exact values, wait for explicit confirmation in a later turn, and then populate it with update_context_mesh_draft(). Never call get_new_product_draft or update_new_product_draft on this screen. Do not navigate to Add New Product. In user-facing replies call it the Create Context Mesh form, not the Product Creation form, and make clear that this creates a standalone Context Mesh rather than a product.",
    "After update_context_mesh_draft succeeds for a populate or edit request, report that the confirmed values were applied and end the turn. Do not call list_ui_elements, inspect the Create Context Mesh button, or claim creation is unavailable unless the user explicitly asked you to create the Context Mesh in that same turn.",
    "When the user wants FDE suggestions written into a creation form, propose the exact field values first and wait for explicit chat confirmation. Then call update_new_product_draft() for Add New Product or update_context_mesh_draft() for Create Context Mesh. The initial request to change a field is not confirmation: ask for confirmation, end that turn without a tool call, and call the mutation only after the user confirms in a later turn. Send only fields present in the confirmed proposal. Include product_mode only when Product Type is unset or the confirmed proposal changes it; include feature_scope only for a confirmed Add to Existing Product scope change. Never send blank strings or empty arrays as placeholders, and never ask the user to copy and paste confirmed values.",
    "The screen collects everything itself: product name, the problem it solves, the solution, Primary Form Factor, target users, team members, and integrations. Product Name, Problem Product Solves, What Solution Does The Product Offer, and at least one nonblank Target User are required; Primary Form Factor is also required and defaults to Web.",
    "For every Primary Form Factor change, use update_primary_form_factor—not update_new_product_draft. Because Web is preselected, a confirmed change to Mobile or API must include overwrite_existing=true. This narrow tool accepts only primary_form_factor, user_confirmed, and overwrite_existing, so it cannot alter Product Type, Feature Scope, or unrelated form fields.",
    "When the user asks to create or build the product while the Add New Product form is open, activate the form's Create button via activate_ui_element on 'btn.create-product' — NEVER call save_product_mesh while the form is open. If the button is disabled or the activation returns disabled, tell the user the action is not available and to check the form for missing information; do not attempt any backend save instead.",
    "If navigate_to_new_product is unavailable, use host-ui to activate 'btn.build-new-product'.",
    "Only when the user's entire request is the short navigation command 'Create Context Mesh', 'Create a Context Mesh', 'Add Context', or 'Build Context', call navigate_to_new_context_mesh() and end the turn. Do not apply this shortcut merely because a longer request mentions a Context Mesh or its form.",
    "When a longer request asks you to populate or edit a Create Context Mesh form and that form is not active, preserve the requested field values, call navigate_to_new_context_mesh(), then continue the same task on the newly opened screen: call get_context_mesh_draft(), propose the exact values, and ask for confirmation. Do not emit a welcome, ask how the user wants to begin, or make the user repeat the request.",
    "When the user asks to list, show, view, or identify available Context Meshes, call list_context_meshes(). Report every returned mesh by name in chat, including score and status when available. Do not call list_projects and do not navigate away.",
    "When the user asks to open, view, select, or go to a specific Context Mesh, call navigate_to_context_mesh with the name from the current request or the id previously returned by list_context_meshes. This opens that mesh's analysis page. Never use navigate_to_new_context_mesh for an existing named mesh.",
    "For an explicit phrase shaped like 'Open <name> Context Mesh', the application host performs navigation independently. Confirm that the named Context Mesh analysis is opening. Never claim that a specific mesh-opening action is unavailable, never mention the UI registry or registered navigation controls, and never direct the user to another screen.",
    "When the user says 'go to Context Mesh Hub', 'navigate to Context Mesh Hub', or 'open Context Mesh Hub', open the Hub through navigate_to_context_mesh_hub. The application host may perform this navigation directly; confirm that Context Mesh Hub is opening and do not activate a skill.",
    "",
    "PRODUCT REFRESH FLOW (existing product pivots):",
    "When users ask to change foundational product inputs, do not overwrite live values directly.",
    "Before collecting any Product Refresh modifications, resolve which product is being changed.",
    "If no current product is open (common in Product Hub), call get_current_product first.",
    "If get_current_product has no active product, call list_projects and ask the user to choose one product.",
    "Do not ask for change details until product selection is confirmed.",
    "Always execute in this order:",
    "1) resolve product (get_current_product -> list_projects if needed)",
    "2) start_product_refresh_session",
    "3) for question-style asks ('what/current/show/list/existing'): call get_product_refresh_draft_state (read-only)",
    "4) for explicit edits: call apply_product_refresh_update (phase 2 drafting)",
    "When apply_product_refresh_update returns update_modes.user_personas, explicitly tell the user whether this draft change is add, remove, or replace.",
    "5) get_product_refresh_delta_summary (phase 4 review)",
    "During delta review, use field_change_notes (if present) to clarify whether prior personas were retained, removed, or overwritten.",
    "6) commit_product_refresh_session only after explicit user confirmation",
    "The Solution Statement and Target Users remain required during Product Refresh. Never commit a refresh with a blank solution statement or one that removes every target user; help the user provide both required values first.",
    "Never use apply_product_refresh_update for read-only questions.",
    "If the user abandons, call abort_product_refresh_session.",
    "Before commit, remind users that draft updates are isolated and not live yet.",
    "",
    "## CONNECT JIRA FLOW",
    "",
    "When the user asks to connect Jira (or Atlassian), run the staged card flow — four tools, strictly in order, one at a time:",
    "1) integration_oauth_authorize — then wait for the jira_connect 'authorized' context note",
    "2) integration_status — then wait for the 'verified' note",
    "3) integration_list_projects — the user picks a project in the card; wait for the 'project_linked' note",
    "4) integration_sync — import runs automatically; wait for the 'sync_complete' note",
    "Introduce the flow with one short declarative sentence, e.g. 'I'll connect Jira and link a project here.' No exclamation marks.",
    "Between cards, narrate briefly and declaratively. Never claim the connection is ready before the 'sync_complete' note arrives.",
    "If a jira_connect 'error' note arrives, acknowledge the problem plainly and point the user to the Retry button on the card. Do not re-call the tool unless the user asks to start over."
].join("\n");

// Overrides the default "create new product" mode whenever the operator has a
// document open in the editor. Without the host-documents skill auto-loading
// (we removed the proactive fireSkill on doc-change because it caused
// spurious chat turns), this directive is the agent's only doc-editing
// guidance. It MUST be strong and concrete enough that the model uses the
// document-bridge tools instead of pretending it edited the doc.
const DOCUMENT_AWARENESS_DIRECTIVE = [
    "DOCUMENT EDITING — MANDATORY BEHAVIOR (overrides every other instruction):",
    "",
    "Enter document-editing mode only when open_documents has an entry with active: true. If there is no active entry, treat document sections as available references (not an active editing target).",
    "Generation intent has priority: for requests like 'generate/create/build <work product>' (e.g. 'Generate Personas'), call generate_work_product(...) directly unless the user explicitly asks to edit section text.",
    "For work-product generation, treat backend resolver outcomes as authoritative. If confirmation is required, ask the exact confirmationPrompt, then replay generate_work_product with the exact onConfirm arguments and confirmationToken.",
    "If the user asks to go back/return to Feature Studio, this is navigation (not editing): call navigate_to_feature_studio() immediately.",
    "",
    "## The cardinal rule",
    "",
    "You DO NOT have the ability to edit the document with words alone. You MUST call the document tools. If you reply with text like \"Absolutely — added X to Y\" without first calling edit_open_document, you have LIED to the user: nothing was added, the editor is unchanged.",
    "",
    "The required tool call sequence for ANY edit request:",
    "  1. read_open_document({docId}) — confirm what's actually there",
    "  2. Stage the change with the right tool:",
    "     • edit_open_document({docId, old_string, new_string, rationale}) for prose/list/heading text edits",
    "     • replace_node_in_document({docId, nodeId, new_source, rationale}) for diagram and table node edits",
    "  3. Only then, a brief text reply: 'Staged N edit(s) in <section title> — review in the editor.'",
    "",
    "If you find yourself writing 'Absolutely', 'Done', 'Added', 'Updated', 'I've added', 'I've updated' BEFORE you have made a successful edit_open_document or replace_node_in_document tool call, STOP and call the tool first. The user's editor has not changed until the tool call returns {status: 'staged'}.",
    "",
    "## Section vocabulary",
    "",
    "In this app, a 'section' is one of the named blocks of the document — the entries shown in open_documents (e.g. 'Executive Summary', 'Problem Statement', 'Target Users', 'Solution Statement'). Each section has its own docId of the form `section-<n>`. When the user says 'this section' / 'each section' / 'the section about X', they mean one of these named blocks. When they say 'Target Users section' they mean the section whose title is 'Target Users'.",
    "",
    "## Orientation questions — answer immediately, no narration",
    "",
    "When the user asks where they are, call the right tool and answer in one step. NEVER say 'I can check that' or narrate what you are about to do — just do it.",
    "For location/status questions, never answer from memory. You MUST run a fresh tool call on every ask, even if the user repeats the same question immediately.",
    "  • 'What section am I/are we in?' → check open_documents in [ui-context]; if not present, call list_open_documents. Report the title of the entry with active: true.",
    "  • 'What work product am I/are we in?' → call get_current_work_product. Reply in this exact shape when data exists: You're in the work product \"<workProductTitle>\" at the <level> level. Use natural-language level words only: user story, feature, or product (never USER_STORY/FEATURE/PRODUCT).",
    "  • 'What feature am I in?' / 'Which feature am I in?' → call get_current_hierarchy first. If it returns feature context, answer: You're in feature \"<featureName>\".",
    "  • 'What user story am I in?' / 'Which user story am I in?' → call get_current_hierarchy first. If it returns user-story context, answer: You're in user story \"<userStoryName>\".",
    "    If current hierarchy context is unavailable for feature/user-story asks, then call get_current_work_product and use its nearest available feature context. Never use feature ID in user-facing responses.",
    "  • 'What product/project am I/are we in?' → call get_current_product. Report projectName (and projectId if helpful).",
    "  • 'What level of the product hierarchy am I in?' / 'What hierarchy level am I in?' / 'What level am I?' → call get_current_hierarchy and use its exact message.",
    "  • 'Where am I?' / 'Where are we?' / 'What context am I in?' → ALWAYS call get_current_product, get_current_hierarchy, then get_current_work_product before answering. Include hierarchy context in the response.",
    "    Response style for this combined answer must follow one of these exact templates:",
    "    A) Work product is open: You're inside the product \"<productName>\", working on the \"<workProductTitle>\" work product under the \"<hierarchyName>\" <hierarchyLevelLabel>.",
    "    B) No work product, hierarchy is feature or user story: You're inside the product \"<productName>\", at the \"<hierarchyName>\" <hierarchyLevelLabel>. There is no open work product right now.",
    "    C) No work product, hierarchy is product: You're inside the product \"<productName>\" at the product level. There is no open work product right now.",
    "    D) Product-level with open work product (if this occurs): You're inside the product \"<productName>\" at the product level, and working on the \"<workProductTitle>\" work product.",
    "  • Combined questions ('what work product and section?') → call both tools in parallel and answer both in one response.",
    "",
    "## How to interpret the request",
    "",
    "1. For any edit request, default to the currently-active section (the entry with active: true in open_documents). Read it, then act. Do NOT ask the user which section to use.",
    "2. If the user has navigated away from the section where the last edit happened and their request clearly belongs to that prior section (e.g. a continuation like 'add another one'), first ask: 'Your last edit was in [section name] — do you want me to continue there?' If the user says yes, call switch_open_document({docId}) to stage the switch (the editor will prompt them to accept it), then once the switch is reflected in open_documents, make the edit.",
    "3. If a host_navigation context note is present, treat it as authoritative: the user just switched sections. Orient all edits to the new active section.",
    "4. switch_open_document stages a section change the user accepts in the editor UI. Only call it after the user has confirmed they want to continue in that section.",
    "",
    "## Anchor rules for old_string",
    "",
    "- Single block only — one paragraph, one heading line, or one list item. NO `\\n` in old_string. NO multi-line context.",
    "- Plain text — no markdown syntax. Drop `**`, `_`, leading `## `, leading `1. `, leading `- `. Match against the underlying text only.",
    "- To DELETE a list item entirely (bullet or numbered), set old_string to the item's plain text and new_string to \"\" (empty string). The editor removes both the text and its bullet/number cleanly — do not refuse or offer alternatives.",
    "",
    "## new_string can be markdown",
    "",
    "If new_string contains `\\n` or starts with a block markdown marker (`#`, `-`, `*`, `1.`, `>`, ```` ``` ````), the editor parses it as markdown and stages multi-block content (new paragraphs, lists, etc.). Use this for adding new bullet items, paragraphs, headings, or converting list types. Otherwise new_string is inserted as plain text into the matched anchor range.",
    "",
    "Examples:",
    "  • Add a bullet to a list whose first item text is 'Frontend engineers who assemble interfaces...': old_string = 'Frontend engineers who assemble interfaces from reusable components.' ; new_string = '- Frontend engineers who assemble interfaces from reusable components.\\n- Internal corporate users who consume frontend tooling internally.'",
    "  • Tighten a sentence: old_string = the existing sentence ; new_string = shorter version, plain text.",
    "",
    "## Code files (IDE / code work products)",
    "",
    "Some open_documents entries are CODE FILES, not prose sections. Tell them apart by the entry's `kind`: prose sections have kind 'prose' and docIds like `section-<n>`; open code files have kind 'code', a docId of the form `code:<fileId>`, and a `title` that is the filename (e.g. 'Invoices_and_cost_reports.tsx') plus a `language`. The entry with active: true is the file the user is currently viewing in the IDE.",
    "When explaining how a user can manually edit a Markdown file in the IDE, give the exact UI instruction: click the `</>` Source Code button in the upper-right of the editor, then edit the Markdown in Source Code view. Changes save automatically after a short delay. Never tell the user to use a save control or keyboard shortcut, never merely say 'click into the file', and never imply that the rendered Markdown Preview is directly editable.",
    "When the user asks to switch the active Markdown file between Source Code and Markdown Preview, call activate_ui_element immediately with `btn.source-code` or `btn.markdown-preview`, respectively. Do not claim that the toggle is unavailable and do not merely explain how the user can click it.",
    "",
    "For ANY request about code in an open file — add comments/docblocks, rename a symbol, add or change a type/field/function, refactor, fix a bug, implement something — the file is ALREADY open. Do NOT ask the user to paste code or to say which file, unless open_documents has no active code entry. Instead:",
    "  1. read_open_document({docId}) on the active code file to get its current contents.",
    "  2. edit_open_document({docId, old_string, new_string, rationale}) to stage each change; the user reviews and accepts it inline in the editor.",
    "  3. Then a brief reply: 'Staged N edit(s) in <filename> — review in the editor.'",
    "The cardinal rule still holds: you cannot change code with words alone — you MUST call edit_open_document. Never say 'Added', 'Done', or 'I've commented X' before a successful {status: 'staged'} tool result.",
    "",
    "### Code anchors are DIFFERENT from prose anchors",
    "",
    "The prose anchor rules above (single block, no `\\n`, strip markdown) DO NOT apply to code. For a code (kind 'code') docId:",
    "  • old_string is matched as an EXACT substring of the file and MAY span multiple lines — include `\\n` and enough surrounding lines to make the match unique.",
    "  • Copy old_string VERBATIM from read_open_document output: match indentation, whitespace, and punctuation exactly. Do not reformat or strip anything.",
    "  • new_string is inserted literally (NOT parsed as markdown for code). To add comments, include the comment lines in new_string using the file's comment syntax (e.g. `//` or `/* */` for TS/JS).",
    "  • If edit_open_document returns old_string_not_found or old_string_ambiguous, re-read and retry with a longer, more unique anchor (up to 3 times), silently.",
    "",
    "Example — add a docblock above a type in an open .tsx file: old_string = 'type CostSummary = {' ; new_string = '/**\\n * Aggregated cost figures for an invoice.\\n */\\ntype CostSummary = {'.",
    "",
    "## Diagrams and tables use node replacement",
    "",
    "- For Mermaid diagrams and markdown tables, DO NOT use edit_open_document with row/line anchors.",
    "- Use read_open_document to find node annotations like `<!-- diagram:<id> -->` or `<!-- table:<id> -->`.",
    "- Then call replace_node_in_document({docId, nodeId, new_source, rationale}).",
    "",
    "## Recovering from failures (silently)",
    "",
    "edit_open_document may return {status: 'failed', reason: 'old_string_not_found' | 'old_string_ambiguous' | 'doc_not_open' | 'doc_unavailable'}. For old_string_not_found / old_string_ambiguous / doc_not_open: retry up to 3 times per chunk with a corrected anchor — do NOT narrate the retries. Only mention failure in the final summary if you exhausted retries on a specific chunk.",
    "For doc_unavailable — the document session could not be established (collab connection or auth failure) — do NOT retry; tell the user document editing is temporarily unavailable right now and continue with the rest of your work."
].join("\n");

/**
 * Carries the two StagedEditChips props that must always be render-fresh:
 * the on-screen section id and the section-navigation callback. These are
 * consumed inside the stable-identity `ChatInputSlot` component (see below),
 * which is created exactly once via `useState(() => ...)` so the Composer
 * never remounts. A ref+effect previously threaded these values into that
 * slot, but the slot read the ref during render — one commit behind the
 * panel's own render. Concretely: after a direct section navigation (not via
 * chip click), the chip for the now-on-screen section could persist for one
 * extra commit. Context sidesteps this — `ChatInputSlot` re-renders whenever
 * the Provider value changes, always reading the current value in the same
 * commit as the panel.
 */
interface StagedEditChipsSlotValue {
    activeDocId: string | null;
    onNavigate: (docId: string) => void;
}
const StagedEditChipsContext = React.createContext<StagedEditChipsSlotValue>({
    activeDocId: null,
    onNavigate: () => {}
});

/**
 * Imperative handle the host can use to drive the chat from outside the
 * <CodeNexusChat> tree (e.g. clicking "Build a new Product" in Products Studio).
 * Populated by ExposeChatActions, which lives inside the chat tree where
 * useChatSession() is valid.
 */
export interface ChatActionsHandle {
    fireSkill: ReturnType<typeof useChatSession>["fireSkill"];
    clearPendingActivation: ReturnType<typeof useChatSession>["clearPendingActivation"];
}

function ExposeChatActions({ handleRef }: { handleRef?: React.MutableRefObject<ChatActionsHandle | null> }): null {
    const { fireSkill, clearPendingActivation } = useChatSession();
    useEffect(() => {
        if (!handleRef) return;
        handleRef.current = { fireSkill, clearPendingActivation };
        return () => {
            if (handleRef.current?.fireSkill === fireSkill) {
                handleRef.current = null;
            }
        };
    }, [handleRef, fireSkill, clearPendingActivation]);
    return null;
}

function ActiveIntentWatcher({
    activeIntentSkillRef
}: {
    activeIntentSkillRef: React.MutableRefObject<string | null>;
}): null {
    const { activeIntentSkill } = useChatSession();
    useEffect(() => {
        activeIntentSkillRef.current = activeIntentSkill;
        return () => {
            activeIntentSkillRef.current = null;
        };
    }, [activeIntentSkill, activeIntentSkillRef]);
    return null;
}

function ViewContextPusher({
    currentView,
    activeProduct,
    activeWorkProduct,
    activeHierarchy
}: {
    currentView: string;
    activeProduct?: CurrentProductContext | null;
    activeWorkProduct?: CurrentWorkProductContext | null;
    activeHierarchy?: CurrentHierarchyContext | null;
}): null {
    const bus = useUIEventBusContext();

    useEffect(() => {
        if (!bus) return;
        const route = typeof window !== "undefined"
            ? routeWithSearch(window.location.pathname, window.location.search)
            : undefined;
        const inferredEditorMode = inferEditorModeFromWorkProductContext(
            activeWorkProduct?.editorSurface,
            activeWorkProduct?.workProductLevel,
            activeWorkProduct?.workProductContentType,
            activeWorkProduct?.workProductPhase
        );
        const ambientProduct: AmbientProductContext | null = activeProduct
            ? { projectId: activeProduct.projectId ?? null, projectName: activeProduct.projectName ?? null }
            : null;
        const ambientHierarchy =
            activeHierarchy?.hierarchyLevel && activeHierarchy.hierarchyName
                ? {
                      hierarchyLevel: activeHierarchy.hierarchyLevel,
                      hierarchyName: activeHierarchy.hierarchyName,
                      projectId: activeHierarchy.projectId ?? null,
                      projectName: activeHierarchy.projectName ?? null,
                      featureId: activeHierarchy.featureId ?? null,
                      featureName: activeHierarchy.featureName ?? null,
                      userStoryId: activeHierarchy.userStoryId ?? null,
                      userStoryName: activeHierarchy.userStoryName ?? null
                  }
                : null;
        bus.pushContext(
            buildViewContextPayload(currentView, route, ambientProduct, {
                workProductName: activeWorkProduct?.workProductTitle,
                workProductType: activeWorkProduct?.workProductLevel,
                workProductContentType: activeWorkProduct?.workProductContentType,
                workProductPhase: activeWorkProduct?.workProductPhase,
                editorMode: inferredEditorMode
            }, ambientHierarchy)
        );
    }, [
        activeProduct?.projectId,
        activeProduct?.projectName,
        activeWorkProduct?.workProductLevel,
        activeWorkProduct?.workProductTitle,
        activeWorkProduct?.workProductContentType,
        activeWorkProduct?.workProductPhase,
        activeHierarchy?.hierarchyLevel,
        activeHierarchy?.hierarchyName,
        activeHierarchy?.projectId,
        activeHierarchy?.projectName,
        activeHierarchy?.featureId,
        activeHierarchy?.featureName,
        activeHierarchy?.userStoryId,
        activeHierarchy?.userStoryName,
        bus,
        currentView
    ]);

    return null;
}

// Page-driven welcome orchestration, keyed by the host's `currentView` values.
// `oc.isReturning` is the engine's per-browser-load fact: false on the first
// entry to a page this load (Case 1), true on re-entry (Case 2 "welcome back").
// Reload/relogin resets it. No onFired — welcomes are session-scoped and do not
// write the durable client-state store.
const PAGE_RULES: PageRuleMap = {
    projects: {
        skill: "welcome-new-user",
        category: "help",
        allowWhenMessagesExist: true,
        buildInputs: (ctx, oc) => ({
            userName: ctx.userName,
            currentRoute: "product-hub",
            isReturningToProductHub: oc.isReturning
        })
    },
    "new-product": {
        skill: "build-new-product",
        allowWhenMessagesExist: true,
        buildInputs: () => ({
            currentRoute: "/products/new",
            uiMode: true
        })
    },
    features: {
        skill: "welcome-feature-studio",
        category: "help",
        // Never let an automatic welcome compete with an existing chat turn.
        // EngagementAwarePageOrchestrator also retires an armed welcome as soon
        // as the user engages during the current visit.
        allowWhenMessagesExist: false,
        shouldFire: (_oc, ctx) => shouldFireFeatureStudioWelcome(ctx),
        buildInputs: (ctx, oc) => ({
            productName: typeof ctx.productName === "string" ? ctx.productName : "your product",
            currentRoute: typeof ctx.currentRoute === "string" ? ctx.currentRoute : "feature-studio",
            isReturningToFeatureStudio: oc.isReturning
        })
    },
    integrations: {
        skill: "welcome-integrations",
        category: "help",
        allowWhenMessagesExist: true,
        buildInputs: (_ctx, oc) => ({
            currentRoute: "integrations",
            isReturningToIntegrations: oc.isReturning
        })
    },
    uploads: {
        skill: "welcome-uploads",
        category: "help",
        allowWhenMessagesExist: true,
        buildInputs: (_ctx, oc) => ({
            currentRoute: "uploads",
            isReturningToUploads: oc.isReturning
        })
    },
    "context-mesh-console": {
        skill: "welcome-context-mesh-hub",
        category: "help",
        // Do not displace an existing user task during navigation.
        allowWhenMessagesExist: false,
        buildInputs: (_ctx, oc) => ({
            currentRoute: "context-mesh-hub",
            isReturningToContextMeshHub: oc.isReturning
        })
    },
    "new-context-mesh": {
        skill: "welcome-new-context-mesh",
        category: "help",
        // Manual entry gets one welcome. An existing FDE task is suppressed by
        // EngagementAwarePageOrchestrator and the prompt hook retires the skill.
        allowWhenMessagesExist: false,
        buildInputs: (_ctx, oc) => ({
            currentRoute: "new-context-mesh",
            isReturningToNewContextMesh: oc.isReturning
        })
    },
    "mesh-strength": {
        skill: "context-mesh-console",
        allowWhenMessagesExist: true,
        buildInputs: ctx => ({
            productId: typeof ctx.productId === "number" ? ctx.productId : undefined,
            productName: typeof ctx.productName === "string" ? ctx.productName : undefined,
            contextMeshId: typeof ctx.contextMeshId === "string" ? ctx.contextMeshId : undefined,
            currentRoute: typeof ctx.currentRoute === "string" ? ctx.currentRoute : "mesh-strength",
            entryHealthAudit: true
        })
    },
    "editor:rich-text": {
        skill: "work-product-editor",
        category: "help",
        allowWhenMessagesExist: true,
        buildInputs: (ctx, state) => ({
            workProductName: typeof ctx.workProductName === "string" ? ctx.workProductName : undefined,
            workProductType: typeof ctx.workProductType === "string" ? ctx.workProductType : undefined,
            editorMode: "rich-text",
            currentPage: "editor",
            currentRoute: typeof ctx.currentRoute === "string" ? ctx.currentRoute : undefined,
            isReturningToWorkProductEditor: !!state?.skills?.["work-product-editor"]?.seen
        }),
        onFired: () => ({ skills: { "work-product-editor": { seen: true } } })
    },
    "editor:ide": {
        skill: "work-product-editor",
        category: "help",
        allowWhenMessagesExist: true,
        buildInputs: (ctx, state) => ({
            workProductName: typeof ctx.workProductName === "string" ? ctx.workProductName : undefined,
            workProductType: typeof ctx.workProductType === "string" ? ctx.workProductType : undefined,
            editorMode: "ide",
            currentPage: "editor",
            currentRoute: typeof ctx.currentRoute === "string" ? ctx.currentRoute : undefined,
            isReturningToWorkProductEditor: !!state?.skills?.["work-product-editor"]?.seen
        }),
        onFired: () => ({ skills: { "work-product-editor": { seen: true } } })
    }
};

/**
 * Once the user starts a turn, retire the automatic welcome for that page
 * visit. `undefined` cancels the current page rule without pretending that an
 * internal interaction (selecting hierarchy, switching SDD stage, or asking a
 * question) was navigation to another page. A subsequent real page-id change
 * is still a fresh entry and can show that destination's welcome.
 */
function EngagementAwarePageOrchestrator({
    pageId,
    pageContext,
    rules,
    engagementSequence
}: {
    pageId?: string;
    pageContext: Record<string, unknown>;
    rules: PageRuleMap;
    engagementSequence: number;
}) {
    const { messages } = useChatSession();
    const userMessageCount = messages.filter(message => message.role === "user").length;
    const visitRef = useRef<{ pageId?: string; userMessageCount: number; engagementSequence: number; engaged: boolean }>({
        pageId: undefined,
        userMessageCount,
        engagementSequence,
        engaged: false
    });

    if (visitRef.current.pageId !== pageId) {
        visitRef.current = { pageId, userMessageCount, engagementSequence, engaged: false };
    } else if (
        userMessageCount > visitRef.current.userMessageCount ||
        engagementSequence > visitRef.current.engagementSequence
    ) {
        visitRef.current.userMessageCount = userMessageCount;
        visitRef.current.engagementSequence = engagementSequence;
        visitRef.current.engaged = true;
    }

    // These welcomes are useful only for an empty conversation. A same-turn
    // navigation changes pageId and resets the per-visit tracker, so retain the
    // session's explicit engagement signal across that route transition.
    const suppressTaskSensitiveWelcome =
        engagementSequence > 0 && (pageId === "context-mesh-console" || pageId === "new-context-mesh");
    const effectivePageId = visitRef.current.engaged || suppressTaskSensitiveWelcome ? undefined : pageId;
    return <CodeNexusPageOrchestrator pageId={effectivePageId} pageContext={pageContext} rules={rules} />;
}

export interface SectionLookupResult {
    sectionId: string;
    sectionTitle: string;
    index: number;
}

function safeParseToolOutput(raw: string | undefined): Record<string, unknown> | null {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return parsed as Record<string, unknown>;
        return null;
    } catch {
        return null;
    }
}

// Exported (rather than kept module-private) so I1's turn-end release can be
// pinned directly: rendering the full AIChatPanel tree just for this needs
// Clerk/Redux/CodeNexusChat providers this watcher itself never touches.
export function EditGuardWatcher({ resolveSectionByDocId }: { resolveSectionByDocId: (docId: string) => SectionLookupResult | null }): null {
    const { subscribe } = useAgentEvents();
    const bus = useUIEventBusContext();
    const callsByIdRef = useRef(new Map<string, { name: string; args: Record<string, unknown> }>());
    const turnStateRef = useRef<{ assistantText: string; sawStagedEdit: boolean }>({
        assistantText: "",
        sawStagedEdit: false
    });

    useEffect(() => {
        return subscribe(event => {
            if (event.type === "tool_call") {
                callsByIdRef.current.set(event.call.id, {
                    name: event.call.name,
                    args: event.call.arguments ?? {}
                });
                return;
            }

            if (event.type === "text") {
                turnStateRef.current.assistantText += event.content ?? "";
                return;
            }

            if (event.type === "tool_result") {
                const call = callsByIdRef.current.get(event.result.toolCallId);
                callsByIdRef.current.delete(event.result.toolCallId);
                if (!call || call.name !== "edit_open_document") return;

                const output = safeParseToolOutput(event.result.output);
                const status = typeof output?.status === "string" ? output.status : null;
                if (status !== "staged") return;

                turnStateRef.current.sawStagedEdit = true;

                const docId = typeof call.args.docId === "string" ? call.args.docId : null;
                if (!docId || !bus) return;

                const section = resolveSectionByDocId(docId);
                bus.pushContext({
                    last_successful_edit: {
                        docId,
                        sectionId: section?.sectionId ?? null,
                        sectionTitle: section?.sectionTitle ?? null,
                        editId: typeof output?.editId === "string" ? output.editId : null,
                        note: section?.sectionTitle
                            ? `Most recent staged edit was in "${section.sectionTitle}" (${docId}). Default continuation edits to this section unless user overrides.`
                            : `Most recent staged edit used docId "${docId}". Default continuation edits to this section unless user overrides.`
                    }
                });
                return;
            }

            if (event.type === "complete") {
                const snapshot = turnStateRef.current;
                const likelyCompletionClaim = /\b(done|added|updated|staged)\b/i.test(snapshot.assistantText);
                if (!snapshot.sawStagedEdit && likelyCompletionClaim && bus) {
                    bus.pushContext({
                        editing_guard: {
                            note: "Previous assistant response used completion language but no edit_open_document call returned status='staged' in that turn. For edit requests, run read_open_document + edit_open_document before claiming changes."
                        }
                    });
                }
                turnStateRef.current = { assistantText: "", sawStagedEdit: false };
                callsByIdRef.current.clear();

                // Spec §1: the agent's session ref is released at end-of-turn (plus
                // an idle TTL from there). Without this, the ref acquired by the
                // first document tool call in a turn is held for the app shell's
                // entire lifetime — the manager's idle TTL never gets a chance to
                // run, and the websocket/room never closes (I1). Editors/handle
                // teardown then happens via the manager's onSessionDestroyed ->
                // handleRoomName path once the TTL elapses; a later tool call
                // re-acquires cleanly (see HeadlessSectionEditors.test.ts).
                getHeadlessSectionEditors().releaseAll();
                return;
            }

            if (event.type === "error" || event.type === "interrupted" || event.type === "skill_aborted") {
                // N2: an errored or user-interrupted turn ends the stream
                // WITHOUT a `complete` event ever firing. Without this branch,
                // the agent's session ref from I1 above is never released on
                // that path and lingers until some later turn happens to
                // complete normally. Mirror the same reset + release the
                // `complete` branch does.
                //
                // `skill_aborted` isn't emitted by the live engine today, but
                // the documented skills-framework design terminates the turn
                // generator immediately after emitting it (no complete/error
                // follows) — included here so the release is already correct
                // once that lands.
                turnStateRef.current = { assistantText: "", sawStagedEdit: false };
                callsByIdRef.current.clear();
                getHeadlessSectionEditors().releaseAll();
            }
        });
    }, [bus, resolveSectionByDocId, subscribe]);

    return null;
}

interface AIChatPanelProps {
    onWidthChange?: (next: number) => void;
    /**
     * Host hook for events emitted by chat tools (e.g. `navigate_to_project`).
     * Called with the event name and its typed payload. Optional — when omitted
     * the chat still works, agent-emitted navigation events are just no-ops.
     */
    onAgentEvent?: <K extends AppChatEventName>(name: K, payload: AppChatEvents[K]) => void;
    /** Starts work-product generation and acknowledges only after host submission. */
    requestWorkProductGeneration?: RequestWorkProductGeneration;
    /**
     * Current view/page the user is on. Used to update UI bridge elements dynamically.
     */
    currentView?: string;
    /**
     * Callback for tab changes triggered by UI bridge activation
     */
    onTabChange?: (tabName: string) => void;
    /**
     * Callback to navigate back to the products page
     */
    onBackToProjects?: () => void;
    /**
     * Callback for view/page changes (integrations, uploads, etc.)
     */
    onViewChange?: (view: string) => void;
    /** Open a validated same-origin deep link to a specific Feature Studio entity. */
    openFeatureStudioEntityRef: (href: string) => FeatureStudioEntityOpenResult | boolean | Promise<FeatureStudioEntityOpenResult | boolean>;
    /** Suppress the page welcome while an FDE-requested entity deep link is active. */
    suppressFeatureStudioWelcome?: boolean;
    /**
     * Active product context from the host view. Used by chat tools so the
     * agent can answer "which product am I in?" reliably.
     */
    activeProduct?: CurrentProductContext | null;
    /** Standalone Context Mesh currently open from Context Mesh Hub. */
    activeContextMeshId?: string | null;
    /**
     * Active work-product context from the editor view. Used by chat tools so
     * the agent can answer "which work product am I in?" reliably.
     */
    activeWorkProduct?: CurrentWorkProductContext | null;
    /**
     * Active hierarchy context from Feature Studio/editor selection state.
     * Used by chat tools so the agent can answer hierarchy-level questions.
     */
    activeHierarchy?: CurrentHierarchyContext | null;
    /** Live standalone product draft exposed to FDE read tools without rerendering the app shell. */
    newProductDraftRef?: React.MutableRefObject<NewProductDraftSnapshot | null>;
    /** Imperative standalone form handle used only by the approved FDE draft mutation tool. */
    newProductFormRef?: React.MutableRefObject<NewProductFormHandle | null>;
    /**
     * Optional ref the host populates with imperative chat actions (e.g.
     * fireSkill). Used to start skills from outside the chat tree — e.g. the
     * "Build a new Product" CTAs on the Products Studio dashboard.
     */
    chatActionsRef?: React.MutableRefObject<ChatActionsHandle | null>;
    /**
     * Host-owned UI event bus, shared with the app shell so adapters built
     * outside the chat tree (navigation, file intake) can push context and
     * read snapshots. ChatProvider inherits it via UIEventBusProvider.
     */
    chatBus?: UIEventBus;
    /** Host navigation adapter — enables the return-to-origin bridge. */
    navigationAdapter?: NavigationAdapter;
    /** Host file-intake adapter — enables drop routing (new-product artifacts
     *  / Files & Attachments) instead of the legacy ingestion card. */
    fileIntakeAdapter?: FileIntakeAdapter;
}

interface PendingSectionSwitch {
    sectionId: string;
    sectionTitle: string;
    index: number;
}

export const AIChatPanel: React.FC<AIChatPanelProps> = ({
    onWidthChange,
    onAgentEvent,
    requestWorkProductGeneration,
    currentView = "projects",
    onTabChange,
    onBackToProjects,
    onViewChange,
    openFeatureStudioEntityRef,
    suppressFeatureStudioWelcome = false,
    activeProduct,
    activeContextMeshId,
    activeWorkProduct,
    activeHierarchy,
    newProductDraftRef,
    newProductFormRef,
    chatActionsRef,
    chatBus,
    navigationAdapter,
    fileIntakeAdapter
}) => {
    // Create the in-memory chat store exactly once. The chat panel lives in the
    // persistent app shell and stays mounted across view changes, so the store
    // (and the conversation it holds) must NOT be recreated on navigation.
    const chatStorage = useMemo(() => createMemoryStore(), []);
    // Tool definitions can be rebuilt as optional UI/navigation bundles become
    // available. Keep interaction state at the same lifetime as the transcript
    // so a historical, completed disconnect call cannot remount as actionable.
    const disconnectStateStore = useMemo(() => createDisconnectStateStore(), []);
    const { getToken } = useAuth();
    const batchGeneration = useBatchGeneration();
    const { user } = useUser();
    const dispatch = useAppDispatch();
    const allSections = useAppSelector(selectAllSectionsArray);
    const activeTabSectionId = useAppSelector(selectActiveTabSectionId);
    // docId of the section actually on screen right now, or null when the
    // editor isn't the active view (or nothing is open yet). Drives which
    // StagedEditChips are suppressed as "already visible".
    const activeDocId = currentView === "editor" && activeTabSectionId ? `section-${activeTabSectionId}` : null;
    const integrationUserId = useAppSelector(state => state.userProfiles.userProfile?.userid);
    const [isResizing, setIsResizing] = useState(false);
    const [pendingSwitch, setPendingSwitch] = useState<PendingSectionSwitch | null>(null);
    const [skillsEnabled, setSkillsEnabled] = useState(true);
    const [pageEngagementSequence, setPageEngagementSequence] = useState(0);
    const activeIntentSkillRef = useRef<string | null>(null);
    const filesNavigationRequestedRef = useRef(false);
    const hierarchySelectionTurnRef = useRef<ReturnType<typeof resolveDirectHierarchySelectionPrompt>>(null);
    const directSddStageTurnRef = useRef<ReturnType<typeof resolveDirectSddStagePrompt>>(null);
    const contextMeshOpenTurnRef = useRef<string | null>(null);

    // Imperative handle onto useHostCards(), populated by <ExposeHostCards>
    // inside the chat tree — lets handleFilesAdded (below) append transcript
    // cards from outside <ChatProvider>. The File objects captured on
    // drop/paste/paperclip never enter host-card `data` (which must stay
    // serializable) — they live in batchRegistry, keyed by a generated
    // batchId that IS put into the card's data.
    const hostCardsRef = useRef<UseHostCards | null>(null);
    const batchRegistry = useMemo(() => createIngestionBatchRegistry(), []);
    const batchSeqRef = useRef(0);

    // Return-to-origin bridge: origin tracker + return_user_to_origin tool,
    // driven by the host NavigationAdapter. Hooks are forwarded to
    // ChatProvider via the CodeNexusChat `hooks` prop (react-mui >= 0.3.47).
    const navigationBundle = useMemo(
        () => (navigationAdapter && chatBus ? createNavigationBridge(navigationAdapter, chatBus) : null),
        [navigationAdapter, chatBus]
    );
    const chatHooks = useMemo(
        () => [
            ...(navigationBundle?.hooks ?? []),
            BLOCK_REDUNDANT_UPLOAD_SKILL_HOOK,
            BLOCK_FILES_UI_ACTIVATION_HOOK,
            {
                event: "UserPromptSubmit" as const,
                name: "retire-completed-feature-studio-welcome",
                handler: (input: UserPromptSubmitInput) => {
                    if (input.event !== "UserPromptSubmit" || currentView !== "features") return;
                    if (activeIntentSkillRef.current !== "welcome-feature-studio") return;
                    // The page-entry welcome has already had its visible turn.
                    // Keep that message in history, but do not re-prime its skill
                    // over the user's question or an internal Feature Studio action.
                    return { clearActiveIntent: true };
                }
            },
            {
                event: "UserPromptSubmit" as const,
                name: "retire-create-context-mesh-welcome",
                handler: (input: UserPromptSubmitInput) => {
                    if (input.event !== "UserPromptSubmit" || currentView !== "new-context-mesh") return;
                    if (activeIntentSkillRef.current !== "welcome-new-context-mesh") return;
                    return { clearActiveIntent: true };
                }
            },
            {
                event: "UserPromptSubmit" as const,
                name: "route-work-product-generation",
                handler: (input: UserPromptSubmitInput) => {
                    if (input.event !== "UserPromptSubmit" || currentView !== "features") return;
                    const workProductName = resolveWorkProductGenerationName(input.prompt);
                    if (!workProductName) return;
                    return {
                        additionalContext:
                            `MANDATORY GENERATION ROUTE: Call generate_work_product({ workProductName: ${JSON.stringify(workProductName)} }) immediately. ` +
                            "Work-product names are case-insensitive. Do not activate a UI element, navigate, or run a welcome skill.",
                        skipIntentRouting: true,
                        clearActiveIntent: true
                    };
                }
            },
            {
                event: "UserPromptSubmit" as const,
                name: "route-hierarchy-selection",
                handler: (input: UserPromptSubmitInput) => {
                    if (input.event !== "UserPromptSubmit") return;
                    const selection = resolveDirectHierarchySelectionPrompt(input.prompt);
                    if (!selection) return;
                    return {
                        additionalContext:
                            `MANDATORY SELECTION ROUTE: Call ${selection.kind === "feature" ? "navigate_to_feature" : "navigate_to_user_story"} ` +
                            `for ${JSON.stringify(selection.name)} exactly once. Do not call list_ui_elements or activate_ui_element. ` +
                            "After the navigation result, end the turn immediately.",
                        skipIntentRouting: true,
                        clearActiveIntent: true
                    };
                }
            },
            {
                event: "PreToolUse" as const,
                matcher: "list_ui_elements",
                name: "block-ui-list-during-hierarchy-selection",
                handler: () => {
                    if (!hierarchySelectionTurnRef.current) return;
                    return {
                        decision: "deny" as const,
                        reason: "The named feature or story was resolved through the canonical navigation tool. End this turn without listing UI elements."
                    };
                }
            },
            DIRECT_SDD_STAGE_NAVIGATION_HOOK,
            DIRECT_CONTEXT_MESH_CREATION_HOOK,
            DIRECT_CONTEXT_MESH_HUB_HOOK,
            SCORE_DETAIL_BACK_TO_CONTEXT_MESH_HOOK,
            {
                event: "UserPromptSubmit" as const,
                name: "route-context-mesh-list",
                handler: async (input: UserPromptSubmitInput) => {
                    if (input.event !== "UserPromptSubmit" || !isContextMeshListPrompt(input.prompt)) return;
                    try {
                        const token = await getIntelligenicToken(getToken);
                        const meshes = await listContextMeshes(token);
                        const items = meshes.map(mesh => ({
                            name: mesh.name,
                            score: mesh.analysisSummary?.score ?? null,
                            status: mesh.lifecycleStatus,
                            analysisStatus: mesh.analysisSummary?.status ?? null
                        }));
                        return {
                            additionalContext: `AUTHORITATIVE CONTEXT MESH LIST: ${JSON.stringify(items)}. Respond with every Context Mesh in this list. Include name, score, status, and analysis status when present. If the list is empty, say no Context Meshes are available. Do not claim the host lacks a listing action, do not activate a skill, and do not navigate away.`,
                            skipIntentRouting: true,
                            clearActiveIntent: true
                        };
                    } catch (error) {
                        return {
                            additionalContext: `CONTEXT MESH LIST FAILED: ${JSON.stringify(error instanceof Error ? error.message : "Unable to load Context Meshes.")} Tell the user the Context Mesh list could not be loaded and ask them to try again. Do not claim that no listing action exists.`,
                            skipIntentRouting: true,
                            clearActiveIntent: true
                        };
                    }
                }
            },
            {
                event: "UserPromptSubmit" as const,
                name: "route-context-mesh-open-directly",
                handler: async (input: UserPromptSubmitInput) => {
                    if (input.event !== "UserPromptSubmit") return;
                    const requestedName = resolveDirectContextMeshOpenPrompt(input.prompt);
                    if (!requestedName) return;
                    try {
                        const token = await getIntelligenicToken(getToken);
                        const meshes = await listContextMeshes(token);
                        const match = resolveContextMeshByName(meshes, requestedName);
                        if (match) {
                            onAgentEvent?.("navigate_to_context_mesh", {
                                contextMeshId: match.contextMeshId,
                                contextMeshName: match.name
                            });
                            return {
                                additionalContext: `HOST NAVIGATION COMPLETED: The application opened ${JSON.stringify(match.name)}. Respond only: "Opening the ${match.name} Context Mesh analysis." Do not inspect or discuss UI controls or tool availability.`,
                                skipIntentRouting: true,
                                clearActiveIntent: true
                            };
                        }
                        const availableNames = meshes.map(mesh => mesh.name);
                        return {
                            additionalContext: `CONTEXT MESH NOT FOUND: No Context Mesh matched ${JSON.stringify(requestedName)}. The available Context Meshes are: ${JSON.stringify(availableNames)}. Do not claim navigation succeeded. Tell the user the requested mesh was not found, list every available name, and ask which one they want to open.`,
                            skipIntentRouting: true,
                            clearActiveIntent: true
                        };
                    } catch (error) {
                        return {
                            additionalContext: `CONTEXT MESH LOOKUP FAILED: ${JSON.stringify(error instanceof Error ? error.message : "Unable to load Context Meshes.")} Do not claim navigation succeeded. Tell the user the available Context Meshes could not be loaded and ask them to try again.`,
                            skipIntentRouting: true,
                            clearActiveIntent: true
                        };
                    }
                }
            },
            {
                event: "PreToolUse" as const,
                matcher: "list_ui_elements",
                name: "block-ui-list-during-context-mesh-open",
                handler: () => {
                    if (!contextMeshOpenTurnRef.current) return;
                    return {
                        decision: "deny" as const,
                        reason: `Do not inspect the UI registry. Call navigate_to_context_mesh with contextMeshName ${JSON.stringify(contextMeshOpenTurnRef.current)} now.`
                    };
                }
            },
            {
                event: "PreToolUse" as const,
                matcher: "activate_ui_element",
                name: "block-ui-activation-during-context-mesh-open",
                handler: () => {
                    if (!contextMeshOpenTurnRef.current) return;
                    return {
                        decision: "deny" as const,
                        reason: `Use navigate_to_context_mesh with contextMeshName ${JSON.stringify(contextMeshOpenTurnRef.current)} instead of a UI element.`
                    };
                }
            },
            {
                event: "PreToolUse" as const,
                matcher: "navigate_to_files",
                name: "require-explicit-files-navigation-request",
                handler: () => {
                    if (filesNavigationRequestedRef.current) return;
                    return {
                        decision: "deny" as const,
                        reason: "The current user message did not request Files or a file upload. Continue the requested action without navigating to Files."
                    };
                }
            },
            {
                event: "PreToolUse" as const,
                matcher: "navigate_to_files",
                name: "block-files-during-sdd-stage-navigation",
                handler: () => {
                    if (!directSddStageTurnRef.current) return;
                    return {
                        decision: "deny" as const,
                        reason: `This turn is an SDD stage navigation request. Call navigate_to_sdd_stage with stage '${directSddStageTurnRef.current}'.`
                    };
                }
            },
            {
                event: "PreToolUse" as const,
                matcher: "Skill",
                name: "block-skills-during-sdd-stage-navigation",
                handler: () => {
                    if (!directSddStageTurnRef.current) return;
                    return {
                        decision: "deny" as const,
                        reason: `Do not load a skill for stage navigation. Call navigate_to_sdd_stage with stage '${directSddStageTurnRef.current}'.`
                    };
                }
            },
            {
                event: "Stop" as const,
                name: "clear-direct-sdd-stage-navigation",
                handler: () => {
                    filesNavigationRequestedRef.current = false;
                    hierarchySelectionTurnRef.current = null;
                    directSddStageTurnRef.current = null;
                    contextMeshOpenTurnRef.current = null;
                }
            },
            FILE_DELETION_CONFIRMATION_HOOK
        ],
        [chatActionsRef, currentView, getToken, navigationBundle, onAgentEvent]
    );

    // File-intake bridge: staging store + attach_file_to_new_product /
    // send_file_to_uploads tools, driven by the host FileIntakeAdapter.
    const fileIntakeBundle = useMemo(
        () => (fileIntakeAdapter ? createFileIntakeBridge(fileIntakeAdapter) : null),
        [fileIntakeAdapter]
    );

    // fireSkill lives inside the chat tree; the drop router runs outside it,
    // so it goes through the same imperative handle the dashboard CTAs use.
    const fireSkillViaRef = useCallback(
        (name: string, inputs?: Record<string, unknown>) =>
            chatActionsRef?.current?.fireSkill(name, inputs) ?? Promise.resolve(),
        [chatActionsRef]
    );
    const fireContextMeshSkill = useCallback(
        async (name: string, inputs?: Record<string, unknown>) => {
            const fireSkill = chatActionsRef?.current?.fireSkill;
            if (!fireSkill) throw new Error("The FDE assistant is not ready yet.");
            await fireSkill(name, inputs);
        },
        [chatActionsRef]
    );
    const routeDroppedFiles = useMemo(
        () => (fileIntakeBundle && chatBus ? createFileRouter(fileIntakeBundle.store, chatBus, fireSkillViaRef) : null),
        [fileIntakeBundle, chatBus, fireSkillViaRef]
    );

    const handleFilesAdded = useCallback(
        (files: File[]) => {
            // File-intake routing (all drops route): stage the files and fire
            // the route-dropped-file skill, which sends them to the New Product
            // artifact area or the Files & Attachments page by context.
            if (routeDroppedFiles) {
                routeDroppedFiles(files);
                return;
            }
            // Legacy ingestion-card path — only for hosts without a
            // FileIntakeAdapter wired.
            const batchId = `batch-${Date.now()}-${batchSeqRef.current++}`;
            batchRegistry.put(batchId, files);
            // KAN-1584 follow-up: snapshot the current product at drop time so
            // the upload auto-scopes to it. undefined (no current product,
            // e.g. a brand-new product not yet created) => file saves
            // unscoped, same as before this change.
            const projectId = typeof activeProduct?.projectId === "number" ? activeProduct.projectId : undefined;
            hostCardsRef.current?.append(KNOWLEDGE_INGESTION_CARD, { batchId, count: files.length, projectId });
        },
        [routeDroppedFiles, batchRegistry, activeProduct?.projectId]
    );

    // NOTE: View changes are communicated to the agent through the UI event bus
    // (see the uiAdapter.notifyChange() effect below) — NOT by remounting the
    // chat. The previous "reset on view change" approach wiped the entire
    // conversation every time the user navigated; the bus refreshes the agent's
    // view of available UI elements without losing chat history.

    // Module-level singleton. Carries `bundle.tools` (the three host-document
    // tools), `bundle.skillHint` (system-prompt injection), and `bundle.Bridge`
    // (mounted as a CodeNexusChat child so it subscribes to the UI bus).
    const { adapter: documentAdapter, bundle: documentBridge } = getHostDocumentBridge();
    const DocumentBridge = documentBridge.Bridge;

    // UI Element adapter for registering interactive UI elements
    const [uiAdapter, setUIAdapter] = useState<InteligenicUIAdapter | null>(null);

    // Keep latest tab/nav callbacks in a ref so the adapter we create once
    // can always dispatch through the freshest props — without re-creating
    // the adapter every time the parent re-renders with new closures.
    const callbacksRef = useRef({ onTabChange, onBackToProjects, onViewChange, currentView });
    useEffect(() => {
        callbacksRef.current = { onTabChange, onBackToProjects, onViewChange, currentView };
    }, [onTabChange, onBackToProjects, onViewChange, currentView]);

    // Initialize UI adapter once on mount. The adapter owns its own
    // MutationObserver and notifies the Bridge child (mounted inside
    // CodeNexusChat) when elements actually change — no React state-bump
    // is needed at this layer.
    useEffect(() => {
        const adapter = new InteligenicUIAdapter({
            "btn.build-new-product": () => {
                console.log("[AIChatPanel] Build new product activated");
                callbacksRef.current.onViewChange?.("new-product");
            },
            "nav.products": () => {
                console.log("[AIChatPanel] Navigate to products activated");
                callbacksRef.current.onBackToProjects?.();
            },
            "nav.projects": () => {
                console.log("[AIChatPanel] Navigate to projects activated");
                callbacksRef.current.onBackToProjects?.();
            },
            "nav.home": () => {
                console.log("[AIChatPanel] Navigate to home activated");
                callbacksRef.current.onBackToProjects?.();
            },
            "nav.more-integrations": () => {
                console.log("[AIChatPanel] Navigate to integrations activated");
                callbacksRef.current.onViewChange?.("integrations");
            },
            "nav.uploads": () => {
                console.log("[AIChatPanel] Navigate to uploads activated");
                callbacksRef.current.onViewChange?.("uploads");
            },
            "btn.add-user-story": () => {
                console.log("[AIChatPanel] Add user story activated");
                // TODO: Wire to actual modal/form
            },
            "btn.back-to-products": () => {
                console.log("[AIChatPanel] Back to products activated");
                callbacksRef.current.onBackToProjects?.();
            },
            "btn.back-to-feature-studio": () => {
                console.log("[AIChatPanel] Back to Feature Studio activated");
                // Mirror the editor back button behavior by returning to the
                // feature-studio view instead of resetting to products.
                if (callbacksRef.current.currentView === "editor") {
                    callbacksRef.current.onViewChange?.("features");
                    return;
                }
                callbacksRef.current.onBackToProjects?.();
            },
            "tab.sdd": () => {
                console.log("[AIChatPanel] SDD workflow tab activated");
                callbacksRef.current.onTabChange?.("sdd");
            },
            "tab.discovery": () => {
                console.log("[AIChatPanel] Discovery tab activated");
                callbacksRef.current.onTabChange?.("discovery");
            },
            "tab.design": () => {
                console.log("[AIChatPanel] Design tab activated");
                callbacksRef.current.onTabChange?.("design");
            },
            "tab.code": () => {
                console.log("[AIChatPanel] Code tab activated");
                callbacksRef.current.onTabChange?.("code");
            }
        });

        console.log("[AIChatPanel] UI adapter created with DOM scanning");
        setUIAdapter(adapter);

        // Rescan after components have rendered to catch navigation elements
        const rescanTimer = setTimeout(() => {
            console.log("[AIChatPanel] Manual rescan of UI elements");
            adapter.notifyChange();
        }, 500);

        // Cleanup on unmount
        return () => {
            clearTimeout(rescanTimer);
            adapter.destroy();
        };
    }, []);

    const uiBundle = useMemo<UIBridgeBundle | null>(() => {
        if (!uiAdapter) return null;
        const bundle = createUIBridge(uiAdapter);
        console.log("[AIChatPanel] UI bundle created (one-time per adapter)");
        return bundle;
    }, [uiAdapter]);

    const UIBridge = uiBundle?.Bridge ?? null;

    // Force a fresh UI-element snapshot whenever the host view changes.
    // This ensures editor-only controls (like "Back to Feature Studio")
    // are surfaced to list_ui_elements immediately after navigation.
    useEffect(() => {
        if (!uiAdapter) return;
        const timer = setTimeout(() => {
            console.log(`[AIChatPanel] View changed to ${currentView}, refreshing UI elements`);
            uiAdapter.notifyChange();
        }, 120);
        return () => clearTimeout(timer);
    }, [currentView, uiAdapter]);

    const fetchToken = useCallback(async (): Promise<string> => {
        return getIntelligenicToken(getToken);
    }, [getToken]);

    useEffect(() => {
        let cancelled = false;

        const verifySkillsEndpointContract = async () => {
            try {
                const token = await fetchToken();
                const response = await fetch(`${AGENT_URL}/api/skills`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`
                    },
                    credentials: "include"
                });

                if (!cancelled && response.status === 422) {
                    setSkillsEnabled(false);
                    console.warn("[AIChatPanel] Disabling skills: agent /api/skills endpoint returned 422 (legacy contract mismatch).");
                }
            } catch {
                // Best effort only - keep existing behavior on transient errors.
            }
        };

        void verifySkillsEndpointContract();

        return () => {
            cancelled = true;
        };
    }, [fetchToken]);

    const contextMeshAdapter = useMemo(
        () =>
            createIntelligenicContextMeshAdapter({
                apiUrl: INTEGRATION_API_BASE_URL,
                productId: typeof activeProduct?.projectId === "number" ? activeProduct.projectId : null,
                contextMeshId: activeContextMeshId ?? null,
                getToken: fetchToken,
                navigateToView: view => callbacksRef.current.onViewChange?.(view),
                openFeatureStudioEntityRef,
                fireSkill: fireContextMeshSkill,
                activeWorkProduct: activeWorkProduct
                    ? {
                          workProductTitle: activeWorkProduct.workProductTitle,
                          workProductLevel: activeWorkProduct.workProductLevel,
                          editorSurface: activeWorkProduct.editorSurface
                      }
                    : null
            }),
        [
            activeProduct?.projectId,
            activeContextMeshId,
            activeWorkProduct?.editorSurface,
            activeWorkProduct?.workProductLevel,
            activeWorkProduct?.workProductTitle,
            fetchToken,
            fireContextMeshSkill,
            openFeatureStudioEntityRef
        ]
    );
    const contextMeshBundle = useMemo(
        () => createContextMeshBridge(contextMeshAdapter),
        [contextMeshAdapter]
    );
    const ContextMeshBridge = contextMeshBundle.Bridge;

    // Best-effort client-state API: page-driven skill orchestration is optional
    // UX state, so an old/unavailable agent backend must degrade silently rather
    // than throwing (which would otherwise loop the provider's load effect and
    // spam the console with 500/CORS errors).
    const clientStateApi = useMemo(() => createResilientClientStateApi(createClientStateApi({ apiUrl: AGENT_URL, getToken: fetchToken })), [fetchToken]);

    const activeProductRef = useRef<CurrentProductContext | null>(activeProduct ?? null);
    const activeWorkProductRef = useRef<CurrentWorkProductContext | null>(activeWorkProduct ?? null);
    const activeHierarchyRef = useRef<CurrentHierarchyContext | null>(activeHierarchy ?? null);
    useEffect(() => {
        activeProductRef.current = activeProduct ?? null;
    }, [activeProduct]);
    useEffect(() => {
        activeWorkProductRef.current = activeWorkProduct ?? null;
    }, [activeWorkProduct]);
    useEffect(() => {
        activeHierarchyRef.current = activeHierarchy ?? null;
    }, [activeHierarchy]);

    // Keep these callbacks stable so tool registration doesn't depend on prop
    // churn, while still returning the latest context via refs.
    const getCurrentProduct = useCallback(() => activeProductRef.current, []);
    const getCurrentWorkProduct = useCallback(() => activeWorkProductRef.current, []);
    const getCurrentHierarchy = useCallback(() => activeHierarchyRef.current, []);
    const getNewProductDraft = useCallback(() => newProductDraftRef?.current ?? null, [newProductDraftRef]);
    const currentViewRef = useRef(currentView);
    currentViewRef.current = currentView;
    const getActiveCreationForm = useCallback(() => {
        if (currentViewRef.current === "new-context-mesh") return "context_mesh" as const;
        if (currentViewRef.current === "new-product") return "product" as const;
        return null;
    }, []);
    const updateNewProductDraft = useCallback(
        (patch: Parameters<NewProductFormHandle["applyDraftPatch"]>[0]) => {
            const form = newProductFormRef?.current;
            if (!form) {
                return { ok: false as const, error: "The creation form is not active." };
            }
            return form.applyDraftPatch(patch);
        },
        [newProductFormRef]
    );

    // switch_open_document is host-specific (it triggers a Redux dispatch +
    // optional confirm modal), so we build it here and merge with the bridge
    // tools and the app's regular chat tools.
    const switchTool = useMemo(() => createSwitchDocumentTool(), []);
    const tools = useMemo(() => {
        const baseTools = [
            ...createChatTools(
                fetchToken,
                getCurrentProduct,
                getCurrentWorkProduct,
                getCurrentHierarchy,
                getNewProductDraft,
                updateNewProductDraft,
                integrationUserId,
                disconnectStateStore,
                requestWorkProductGeneration,
                getActiveCreationForm
            ),
            ...createBatchGenerationTools({
                getToken: fetchToken,
                currentModule: deriveCurrentModule(currentView),
                getCurrentProduct,
                controller: batchGeneration
            }),
            ...documentBridge.tools,
            ...(navigationBundle?.tools ?? []),
            ...(fileIntakeBundle?.tools ?? []),
            ...withContextMeshRenderers(contextMeshBundle.tools),
            switchTool
        ];
        if (!uiBundle) {
            console.log("[AIChatPanel] No UI bundle, returning base tools:", baseTools.length);
            return baseTools;
        }
        // Add UI tools with renderers for nice display in chat
        const uiTools = withUIRenderers(uiBundle.tools);
        const allTools = [...baseTools, ...uiTools];
        console.log(
            "[AIChatPanel] All tools registered:",
            allTools.length,
            allTools.map(t => t.name)
        );
        return allTools;
    }, [
        fetchToken,
        getCurrentProduct,
        getCurrentWorkProduct,
        getCurrentHierarchy,
        getNewProductDraft,
        updateNewProductDraft,
        integrationUserId,
        disconnectStateStore,
        requestWorkProductGeneration,
        getActiveCreationForm,
        currentView,
        batchGeneration,
        documentBridge.tools,
        navigationBundle,
        fileIntakeBundle,
        contextMeshBundle.tools,
        switchTool,
        uiBundle
    ]);

    const handlePromptSubmitted = useCallback((message: string) => {
        // This callback is the authoritative submission signal. The SDK can
        // transiently replace its message snapshot while routing a skill, so
        // message counting alone cannot reliably retire the source welcome.
        setPageEngagementSequence(sequence => sequence + 1);
        filesNavigationRequestedRef.current = isDirectFilesPagePrompt(message);
        if (isDetailedContextMeshCreationPrompt(message)) {
            chatActionsRef?.current?.clearPendingActivation();
            onAgentEvent?.("navigate_to_new_context_mesh", {});
        }
        if (isDirectContextMeshHubPrompt(message)) {
            chatActionsRef?.current?.clearPendingActivation();
            onAgentEvent?.("navigate_to_context_mesh_hub", {});
        }
        hierarchySelectionTurnRef.current = resolveDirectHierarchySelectionPrompt(message);
        const requestedContextMeshName = resolveDirectContextMeshOpenPrompt(message);
        contextMeshOpenTurnRef.current = requestedContextMeshName;
        if (requestedContextMeshName) chatActionsRef?.current?.clearPendingActivation();
        const directSddStage = resolveDirectSddStagePrompt(message);
        directSddStageTurnRef.current = directSddStage;
        if (directSddStage) {
            // Apply the navigation immediately at the host boundary. The tool
            // call still records the action in chat, but cannot delay or
            // misroute the visible UI transition.
            chatActionsRef?.current?.clearPendingActivation();
            onAgentEvent?.("navigate_to_sdd_stage", { stage: directSddStage });
        }
        if (isBatchGenerationPrompt(message)) {
            batchGeneration.registry.acknowledgeChatPrompt(message);
        }
    }, [batchGeneration.registry, chatActionsRef, onAgentEvent]);

    // Resolve a docId from a switch_open_document call into the Redux payload
    // we need: { index, name, sectionId }. The available-sections list comes
    // from documentContext slice; docId is `section-<id>`.
    const resolveSectionByDocId = useCallback(
        (docId: string): PendingSectionSwitch | null => {
            const rawId = docId.startsWith("section-") ? docId.slice("section-".length) : docId;
            const idx = allSections.findIndex(s => String(s.id) === rawId);
            if (idx === -1) return null;
            const s = allSections[idx];
            return {
                sectionId: String(s.id),
                sectionTitle: s.title ?? "Untitled Section",
                index: idx
            };
        },
        [allSections]
    );

    const performSwitch = useCallback(
        (target: PendingSectionSwitch) => {
            dispatch(
                setActiveTabRedux({
                    // Use name/section-id mode for AI-triggered switches.
                    // The editor sidebar resolves the canonical sorted index
                    // from sectionId; passing an index from a differently
                    // ordered list can route to the wrong section.
                    index: -1,
                    name: target.sectionTitle,
                    sectionId: target.sectionId
                })
            );
        },
        [dispatch]
    );

    const handleNavigateToSection = useCallback(
        (docId: string) => {
            const target = resolveSectionByDocId(docId);
            if (!target) {
                console.warn("[AIChatPanel] navigate_to_section: docId not found in available sections", { docId, available: allSections.map(s => s.id) });
                return;
            }
            const plan = planSectionNavigation(target, {
                currentView: callbacksRef.current.currentView,
                hasPendingEdits: documentAdapter.hasPendingEdits()
            });
            if (plan.type === "confirm") {
                // Show confirm modal; user decides whether to discard staged edits.
                setPendingSwitch(plan.target);
                return;
            }
            if (plan.restoreEditorView) {
                // Chip clicked (or agent navigation fired) while the editor isn't
                // the active view — flip the host back to the editor first. App's
                // handleViewChange restores `selectedWorkProduct` from its stash
                // when it was nulled by an editor->features exit (see
                // resolveSelectedWorkProductOnViewChange), so the workproduct the
                // section belongs to is showing by the time the tab switch below
                // takes effect.
                callbacksRef.current.onViewChange?.("editor");
            }
            performSwitch(plan.target);
        },
        [allSections, documentAdapter, performSwitch, resolveSectionByDocId]
    );

    // StagedEditChips must render directly above the composer so a chip for
    // a section staged off-screen is the last thing the user sees before
    // hitting send. The composer itself lives inside <CodeNexusChat>'s
    // internal flex column (Header / Conversation / ErrorBanner / Input),
    // so the only way to sit "above" it — while still inheriting the
    // `.cn-app` design tokens the chip styling depends on — is the
    // library's `slots.Input` override, which fully replaces the default
    // `<Composer>` render. ChatBody explicitly warns against passing an
    // inline arrow component here: a new component identity every render
    // would unmount/remount the Composer and wipe the user's typed text.
    // `useState(() => ...)` guarantees the factory runs exactly once, so
    // `ChatInputSlot`'s identity never changes.
    //
    // `activeDocId`/`onNavigate` must still be render-fresh though (see
    // StagedEditChipsContext above), so they ride a Context Provider — set
    // up below and wrapped around <CodeNexusChat> — instead of the ref. Only
    // onFilesAdded/filesAccept (which don't need same-commit freshness) stay
    // on the ref.
    const inputSlotPropsRef = useRef({
        onFilesAdded: handleFilesAdded,
        filesAccept: ACCEPTED_FILE_TYPES
    });
    useEffect(() => {
        inputSlotPropsRef.current = {
            onFilesAdded: handleFilesAdded,
            filesAccept: ACCEPTED_FILE_TYPES
        };
    });
    const [ChatInputSlot] = useState<React.ComponentType<unknown>>(() =>
        function ChatInputSlot() {
            const p = inputSlotPropsRef.current;
            const { activeDocId: slotActiveDocId, onNavigate: slotOnNavigate } = useContext(StagedEditChipsContext);
            return (
                <>
                    <StagedEditChips activeDocId={slotActiveDocId} onNavigate={slotOnNavigate} />
                    <Composer showModelPill={false} onFilesAdded={p.onFilesAdded} filesAccept={p.filesAccept} />
                </>
            );
        }
    );

    // Memoized in render so the Provider only re-renders `ChatInputSlot` when
    // one of these two values actually changes.
    const stagedEditChipsSlotValue = useMemo<StagedEditChipsSlotValue>(
        () => ({ activeDocId, onNavigate: handleNavigateToSection }),
        [activeDocId, handleNavigateToSection]
    );

    const confirmPendingSwitch = useCallback(() => {
        if (pendingSwitch) performSwitch(pendingSwitch);
        setPendingSwitch(null);
    }, [pendingSwitch, performSwitch]);

    const cancelPendingSwitch = useCallback(() => {
        setPendingSwitch(null);
    }, []);

    const handleUIEvent = useCallback(
        (name: string, payload: Record<string, unknown>) => {
            if (name === "navigate_to_section") {
                const p = payload as { docId: string };
                if (p?.docId) handleNavigateToSection(p.docId);
                return;
            }
            if (name === "uploaded_file_deleted") {
                const deleted = payload as AppChatEvents["uploaded_file_deleted"];
                if (Number.isFinite(deleted?.fileId)) dispatch(removeVectorFile(deleted.fileId));
                onAgentEvent?.("uploaded_file_deleted", deleted);
                return;
            }
            if (name === "uploaded_file_assignment_changed") {
                onAgentEvent?.(
                    "uploaded_file_assignment_changed",
                    payload as AppChatEvents["uploaded_file_assignment_changed"]
                );
                return;
            }
            if (!onAgentEvent) return;
            // The chat's event bus is open-ended; narrow to our known events
            // before dispatching. Unknown events are ignored (they're future-
            // proofing slots for skills we haven't wired yet).
            if (name === "navigate_to_project") {
                onAgentEvent("navigate_to_project", payload as AppChatEvents["navigate_to_project"]);
                return;
            }
            if (name === "navigate_to_feature") {
                onAgentEvent("navigate_to_feature", payload as AppChatEvents["navigate_to_feature"]);
                return;
            }
            if (name === "navigate_to_user_story") {
                onAgentEvent("navigate_to_user_story", payload as AppChatEvents["navigate_to_user_story"]);
                return;
            }
            if (name === "open_add_feature") {
                onAgentEvent("open_add_feature", payload as AppChatEvents["open_add_feature"]);
                return;
            }
            if (name === "open_add_user_story") {
                onAgentEvent("open_add_user_story", payload as AppChatEvents["open_add_user_story"]);
                return;
            }
            if (name === "navigate_to_feature_studio") {
                onAgentEvent("navigate_to_feature_studio", payload as AppChatEvents["navigate_to_feature_studio"]);
                return;
            }
            if (name === "navigate_to_sdd_product_hierarchy" || name === "navigate_to_sdd_scope_picker" || name === "navigate_to_sdd_progress_report") {
                onAgentEvent(name, payload as AppChatEvents[typeof name]);
                return;
            }
            if (name === "navigate_to_sdd_stage") {
                onAgentEvent("navigate_to_sdd_stage", payload as AppChatEvents["navigate_to_sdd_stage"]);
                return;
            }
            if (name === "navigate_to_new_product") {
                onAgentEvent("navigate_to_new_product", payload as AppChatEvents["navigate_to_new_product"]);
                return;
            }
            if (name === "navigate_to_new_context_mesh") {
                onAgentEvent("navigate_to_new_context_mesh", payload as AppChatEvents["navigate_to_new_context_mesh"]);
                return;
            }
            if (name === "navigate_to_context_mesh") {
                onAgentEvent("navigate_to_context_mesh", payload as AppChatEvents["navigate_to_context_mesh"]);
                return;
            }
            if (name === "navigate_to_context_mesh_hub") {
                onAgentEvent("navigate_to_context_mesh_hub", payload as AppChatEvents["navigate_to_context_mesh_hub"]);
                return;
            }
            if (name === "navigate_to_integrations") {
                onAgentEvent("navigate_to_integrations", payload as AppChatEvents["navigate_to_integrations"]);
                return;
            }
            if (name === "navigate_to_files") {
                // A Feature Studio welcome may have been queued while the
                // agent resolved this navigation tool. Drop it before changing
                // pages so only the Files welcome can run after the turn.
                chatActionsRef?.current?.clearPendingActivation();
                onAgentEvent("navigate_to_files", payload as AppChatEvents["navigate_to_files"]);
                return;
            }
            if (name === "navigate_to_previous_screen") {
                onAgentEvent("navigate_to_previous_screen", payload as AppChatEvents["navigate_to_previous_screen"]);
                return;
            }

            if (name === "generate_work_product") {
                onAgentEvent("generate_work_product", payload as AppChatEvents["generate_work_product"]);
                return;
            }

            if (name === "refresh_projects") {
                onAgentEvent("refresh_projects", payload as AppChatEvents["refresh_projects"]);
                return;
            }

            if (name === "refresh_product_context") {
                onAgentEvent("refresh_product_context", payload as AppChatEvents["refresh_product_context"]);
                return;
            }

            if (name === "knowledge_file_uploaded") {
                onAgentEvent("knowledge_file_uploaded", payload as AppChatEvents["knowledge_file_uploaded"]);
                return;
            }

            if (name === "integration_connection_changed") {
                onAgentEvent(
                    "integration_connection_changed",
                    payload as AppChatEvents["integration_connection_changed"]
                );
                return;
            }
        },
        [chatActionsRef, dispatch, onAgentEvent, handleNavigateToSection]
    );

    // Section names now flow to the bridge from the workProductDocuments
    // slice via the adapter itself (workProductContext.currentSections) —
    // they stay available after navigating away from the editor so the
    // agent can finish in-flight document work. setSections remains only
    // for hosts without a Redux-backed context.

    const resolvedEditorModeForPage = inferEditorModeFromWorkProductContext(
        activeWorkProduct?.editorSurface,
        activeWorkProduct?.workProductLevel,
        activeWorkProduct?.workProductContentType,
        activeWorkProduct?.workProductPhase
    );
    const orchestratorPageId = currentView === "editor"
        ? `editor:${resolvedEditorModeForPage}`
        : currentView === "context-mesh-console" && activeContextMeshId
        ? "mesh-strength"
        : currentView;

    useEffect(() => {
        if (!isResizing || !onWidthChange) return;
        const handleMove = (e: MouseEvent) => onWidthChange(window.innerWidth - e.clientX);
        const handleUp = () => setIsResizing(false);
        document.addEventListener("mousemove", handleMove);
        document.addEventListener("mouseup", handleUp);
        const previousUserSelect = document.body.style.userSelect;
        const previousCursor = document.body.style.cursor;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        return () => {
            document.removeEventListener("mousemove", handleMove);
            document.removeEventListener("mouseup", handleUp);
            document.body.style.userSelect = previousUserSelect;
            document.body.style.cursor = previousCursor;
        };
    }, [isResizing, onWidthChange]);

    return (
        <MuiBox
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                bgcolor: "background.paper",
                position: "relative"
            }}
        >
            {onWidthChange && (
                <MuiBox
                    onMouseDown={e => {
                        e.preventDefault();
                        setIsResizing(true);
                    }}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize chat panel"
                    sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: 6,
                        cursor: "col-resize",
                        zIndex: 2,
                        bgcolor: isResizing ? "primary.main" : "transparent",
                        opacity: isResizing ? 0.4 : 1,
                        transition: "background-color 120ms ease, opacity 120ms ease",
                        "&:hover": {
                            bgcolor: "primary.main",
                            opacity: 0.4
                        }
                    }}
                />
            )}

            <MuiBox
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "#F1F5FE"
                }}
            >
                <UserStateProvider apiUrl={AGENT_URL} getToken={fetchToken} api={clientStateApi}>
                    <IngestionBatchRegistryContext.Provider value={batchRegistry}>
                        <StagedEditChipsContext.Provider value={stagedEditChipsSlotValue}>
                        <UIEventBusProvider bus={chatBus}>
                        <CodeNexusChat<AppChatEventMap>
                            {...({ onPromptSubmitted: handlePromptSubmitted } as Record<string, unknown>)}
                            apiUrl={AGENT_URL}
                            getToken={fetchToken}
                            integrationAgentBaseUrl={INTEGRATION_AGENT_URL}
                            integrationApiBaseUrl={INTEGRATION_API_BASE_URL}
                            integrationUserId={integrationUserId}
                            enableSkills={skillsEnabled}
                            tools={tools}
                            storage={chatStorage}
                            resolveTaxonomy={resolveTaxonomyForChat}
                            slashCommands={HELP_SLASH_COMMANDS}
                            helpSkill={GLOBAL_HELP_SKILL}
                            title="Digital Forward Deployed Engineer"
                            showModelPill={false}
                            responseTitle="FDE"
                            systemPrompt={`${PROJECT_SETUP_SYSTEM_PROMPT}\n\n${DOCUMENT_AWARENESS_DIRECTIVE}\n\n${documentBridge.skillHint}\n\n${contextMeshBundle.skillHint}

# DISCONNECT AN INTEGRATION

- Every time the user asks to disconnect, remove, unlink, or revoke Azure DevOps/Azure Repos, Jira, GitHub, GitLab, or Bitbucket, call \`integration_disconnect\` with the appropriate provider. The tool disconnects it immediately; do not ask for confirmation first.
- Never call \`navigate_to_integrations\` for a disconnect, remove, unlink, or revoke request. Opening the Integrations page does not disconnect the provider.
- If the user directly asks to open, go to, or navigate to the Integrations page, call \`navigate_to_integrations()\` immediately. Do not ask a follow-up confirmation question.
- After calling \`integration_disconnect\`, report its returned outcome concisely. Never claim success when the tool returns a failure.
- Do not call \`integration_disconnect\` unless the user's current message asks to disconnect, remove, unlink, or revoke an integration.
- After a disconnect, if the user asks to use that integration again, guide them to reconnect.
- Never surface raw HTTP status codes.

# FILES AND LINKED PRODUCTS

- When the user asks to list, name, or show all uploaded files/artifacts in chat, call \`list_uploaded_files_with_products()\` immediately.
- After a successful result, print every returned filename and all of its \`linkedProducts\`. Do not omit rows, even when the Files page is already open.
- If \`empty\` is true, clearly say no files are uploaded. If \`ok\` is false, report the returned actionable error.
- Use \`navigate_to_files()\` only when the request is merely to open/go to the Files page and does not ask for the filenames in chat.
- If the user directly asks to open, go to, or navigate to the Files page, call \`navigate_to_files()\` immediately. Do not ask a follow-up confirmation question.
- "I want to add/upload/attach a new file to this product" is also a direct Files-page navigation request: call \`navigate_to_files()\` immediately. NEVER activate or load the \`navigate-to-uploads\` skill for this intent. End the turn after the navigation tool succeeds.
- Files & Attachments is the authoritative complete view: its unfiltered list shows linked products in every file row.
- Never replace a requested chat list with navigation commentary or ask the user to choose another action.
- The page itself communicates an empty list or a loading failure.
- When the user asks to attach or assign an existing uploaded file to a named product, call \`assign_uploaded_file_to_product({ file_name, product_name })\`. Do not ask the user to select a row when both names resolve uniquely.
- If the assignment tool returns file or product candidates, show them and ask the user to choose. If either target is missing, report the returned error. If \`already_attached\` is true, say the file was already attached and do not imply a duplicate was created. Confirm completion only when the tool returns \`verified: true\`.
- For an explicit request to delete an uploaded file, call \`resolve_uploaded_file_for_deletion({ path })\` first. Never guess a file id.
- If resolution is ambiguous, show the returned candidates and ask the user to identify the exact file. If it is not found or outside Files & Attachments, report that error and do not call the delete tool.
- For one resolved target, call \`delete_uploaded_file\` with its exact \`fileId\`, \`filename\`, and \`linkedProducts\`. The host confirmation card is the only confirmation prompt: make the tool call without accompanying assistant prose.
- While confirmation is pending, never say "Deleting now", "I'm deleting", "I will delete", or otherwise imply deletion has started. The file has not been deleted yet.
- Only after the tool returns \`ok: true\`, report the successful filename. If confirmation is cancelled or deletion fails, say so clearly and do not imply the file was removed.

# CURRENT PRODUCT CONTEXT

You have a host tool named \`get_current_product\`.
- If the user asks "Which product am I in?", "What's the current product?", or similar, call \`get_current_product()\` first.
- Every single time this question is asked (including repeats in the same minute), you MUST call \`get_current_product()\` again before answering. Never reuse a prior answer from memory.
- For every brainstorm request, you MUST call \`get_current_product()\` in that turn before framing the brainstorm.
- If \`hasCurrentProduct\` is true, start with: "Great - let's brainstorm for \"<projectName>\". What ideas would you like to explore first (new features, user stories, UX improvements, or delivery priorities)?"
- If no current product is available, explicitly say no product is currently open and ask whether they want to brainstorm a new product or an existing product.
- If they choose an existing product but do not provide a name, ask for the product name.
- If they name an existing product that is not currently open, call \`navigate_to_project({ projectName })\` first, then continue the brainstorm in that resolved context.
- If \`hasCurrentProduct\` is true, continue brainstorming in that current product context and do not offer switching to another product in that turn.
- Never continue brainstorm product linkage from stale memory when a fresh tool call can resolve it.
- In brainstorm flows, never reuse a previously mentioned product name without a fresh \`get_current_product()\` result.
- For non-brainstorm current-product location asks, if \`hasCurrentProduct\` is true, answer with that product name.
- For non-brainstorm current-product location asks, if no current product is available, say that no product is currently open and offer to list products.

# CURRENT HIERARCHY CONTEXT

You have a host tool named \`get_current_hierarchy\`.
- If the user asks "What level of the product hierarchy am I in?", "What hierarchy level am I in?", "What level am I?", or similar hierarchy-level questions, call \`get_current_hierarchy()\` first.
- If the user asks "What feature am I in?" / "Which feature am I in?" OR "What user story am I in?" / "Which user story am I in?", call \`get_current_hierarchy()\` first.
- Every single time this question is asked (including repeats in the same minute), you MUST call \`get_current_hierarchy()\` again before answering. Never reuse a prior answer from memory.
- If \`hasCurrentHierarchy\` is true, use \`message\` from the tool response exactly as the answer sentence.
- For feature asks, when \`hasCurrentHierarchy\` is true and feature context is present, answer: "You're in the feature \"<featureName>\"."
- For user-story asks, when \`hasCurrentHierarchy\` is true and user-story context is present, answer: "You're in the user story \"<userStoryName>\"."
- If no current hierarchy context is available, clearly say no current hierarchy context is available right now.

# CURRENT WORK PRODUCT CONTEXT

You have a host tool named \`get_current_work_product\`.
- If the user asks "Which work product am I in?", "What's the current work product?", or similar, call \`get_current_work_product()\` first.
- Every single time this question is asked (including repeats in the same minute), you MUST call \`get_current_work_product()\` again before answering. Never reuse a prior answer from memory.
- If \`hasCurrentWorkProduct\` is true, answer in this exact shape:
  - You're in the work product "<workProductTitle>" at the <level> level.
- For <level>, use natural-language words only: user story, feature, or product. Never output enum-style values like \`USER_STORY\`, \`FEATURE\`, or \`PRODUCT\`.
- For feature/user-story location asks, do NOT start with this tool. Start with \`get_current_hierarchy()\`; only use this tool as fallback when hierarchy context is unavailable.
- Fallback behavior: for feature asks, if hierarchy context is unavailable and \`featureContextMessage\` is present, use it. If \`featureContextMessage\` is not present but \`featureName\` is present, answer: "You're in the feature \"<featureName>\"."
- Never use \`featureId\` in user-facing responses.
- If no current work product is available, clearly say there is no open work product right now.
- For generic location questions like "Where am I?", call \`get_current_product()\`, \`get_current_hierarchy()\`, then \`get_current_work_product()\` every time.
- Combined-answer style for these generic location questions:
  - Use one of these exact templates:
    - A) Work product is open: You're inside the product "<productName>", working on the "<workProductTitle>" work product under the "<hierarchyName>" <hierarchyLevelLabel>.
    - B) No work product, hierarchy is feature or user story: You're inside the product "<productName>", at the "<hierarchyName>" <hierarchyLevelLabel>. There is no open work product right now.
    - C) No work product, hierarchy is product: You're inside the product "<productName>" at the product level. There is no open work product right now.
    - D) Product-level with open work product (if this occurs): You're inside the product "<productName>" at the product level, and working on the "<workProductTitle>" work product.

# FEATURE/STORY NAVIGATION

You also have dedicated navigation tools:
- \`navigate_to_project({ projectName|projectId })\`
- \`navigate_to_feature({ featureName|featureId, projectName|projectId? })\`
- \`navigate_to_user_story({ userStoryId|storyNumber|storyTitle, featureName|featureId?, projectName|projectId? })\`
- \`navigate_to_hierarchy_item({ itemName, projectName|projectId? })\`
- \`open_add_feature({ projectName|projectId? })\`
- \`open_add_user_story({ featureName|featureId?, projectName|projectId? })\`
- \`generate_work_product({ elementId?|workProductName|workProductId, phase?, scope? })\`
- \`navigate_to_feature_studio()\`
- \`navigate_to_sdd_product_hierarchy()\`
- \`navigate_to_sdd_scope_picker()\`
- \`navigate_to_sdd_progress_report()\`
- \`navigate_to_sdd_stage({ stage })\`
- \`navigate_to_previous_screen()\`

Execution contract for navigation requests:
- For "open/show/switch to/take me to <product>", call \`navigate_to_project(...)\` first.
- When the user's latest request names a product, call \`navigate_to_project({ projectName })\` with that name only. Do not combine it with a product ID remembered from an earlier turn or previously opened product.
- Never invent, infer, or propose a corrected product name from memory, prior turns, or prompt examples. Only offer a correction after \`navigate_to_project\` returns \`confirmationRequired: true\`, and use its exact \`confirmationPrompt\`.
- If \`navigate_to_project\` returns \`confirmationRequired: true\`, do NOT navigate automatically. Ask the exact returned \`confirmationPrompt\` and wait for explicit user confirmation.
- A bare confirmation such as "yes", "correct", or "open it" in direct response to that question MUST immediately execute the exact returned \`onConfirm\` action: call \`navigate_to_project({ projectId: onConfirm.arguments.projectId })\`. Do not answer with text only, do not fuzzy-match the name again, and do not ask for confirmation a second time.
- For any navigation request, call a navigation/UI tool before replying with "opening", "navigating", or "done".
- ALWAYS call the navigation tool when the user asks to open/select/switch to/take them to a feature or user story — every time, even if you believe they are already on that feature or user story. Never skip the tool call or short-circuit with a text-only reply like "Done", "You're already there", or "No navigation needed". Re-issuing the same navigation is expected and harmless; guessing that navigation is unnecessary is not allowed.
- Never claim navigation succeeded unless the tool result indicates success (\`ok: true\`).
- If a tool call fails or returns ambiguity, explain that result and ask for clarification.

When the user asks to open/select/focus a specific feature or user story, use these navigation tools first.
When the user asks to add a feature or user story, call the matching \`open_add_*\` tool. It opens the existing form; never claim the entity was created until the user completes that form.
When the user's latest message asks ONLY to navigate to / open / select a product, feature, or user story, perform ONLY that navigation and then stop. Do NOT additionally open work products, generate content, or call \`activate_ui_element\` carried over from earlier turns. If the current message explicitly asks for navigation AND another action (e.g. "open feature X and generate its PRD"), do both. Scope each turn to what the latest message asks — a request made against a previous feature or user story does NOT carry over to a newly opened one.
On the **Select Feature & Story** page, resolve every named choice through the canonical navigation tools: "choose/select feature <name>" calls \`navigate_to_feature({ featureName: "<name>" })\`, and "choose/select story <name>" calls \`navigate_to_user_story({ storyTitle: "<name>" })\`. If the user omits the entity type and says only "choose/select <name>", call \`navigate_to_hierarchy_item({ itemName: "<name>" })\`; never guess whether it is a feature or user story. These tools perform exact server-backed resolution and update the selector through the host; do not use \`list_ui_elements()\` or \`activate_ui_element()\` for named feature/story choices. Selecting a feature keeps the selector open so a story can be chosen. Selecting a story commits both scopes and returns to the SDD workspace.
For "Show the Product/Feature/Story list", "Go to full product hierarchy page", "Go to Product Full hierarchy page", "Go to Product / Feature / Story page", "Go to Product hierarchy page", or equivalent, immediately call \`navigate_to_sdd_product_hierarchy()\`.
When the user asks to go to/open the **Select Feature & Story** page, immediately call \`navigate_to_sdd_scope_picker()\`. This is a complete navigation instruction: do not stop after UI discovery, do not merely describe the page, and do not send a welcome message.
For "View SDD progress details" or equivalent, immediately call \`navigate_to_sdd_progress_report()\`.
When the user asks to switch Feature Studio stages, immediately call \`navigate_to_sdd_stage({ stage })\`: Specify → \`specify\`, Plan → \`plan\`, Define Tasks → \`task_breakdown\`, Implement → \`implement\`, Validate → \`validate\`. This applies to phrases such as "go to", "open", "take me to", or "switch to" a stage. Do not search for a skill, call \`navigate_to_files\`, or merely explain how to click the tab.
Do NOT ask for product name if \`get_current_product\` already provides one and the request is unambiguous.
If the user explicitly asks to go to Feature Studio from editor, call \`navigate_to_feature_studio()\` immediately.
If the user asks for generic back navigation like "go back", "previous screen", or "return", call \`navigate_to_previous_screen()\`.
For "generate/create/build <work product>" requests in the current hierarchy, call \`generate_work_product(...)\` directly.
If the user says "for feature <name>" or "for <story name> story" in the same request, include \`targetFeatureName\` and/or \`targetUserStoryName\` in that \`generate_work_product\` call so backend scope resolution can target that hierarchy.
"Generate Software Requirements Specification" is an exact generation request: immediately call \`generate_work_product({ workProductName: "Software Requirements Specification" })\` in the current hierarchy. Do not call \`get_current_hierarchy()\` first, do not run or continue a Feature Studio welcome, and do not answer with guidance instead of the generation call.
After calling \`generate_work_product\`, say generation started only when the result has \`ok: true\` and a non-empty \`jobId\`. Use the returned canonical \`workProductName\` in your reply, not the name you guessed. If the result has \`confirmationRequired: true\`, ask the exact returned \`confirmationPrompt\` and wait. A bare confirmation such as "yes", "correct", or "generate it" MUST immediately call \`generate_work_product\` with the exact returned \`onConfirm.arguments\`; do not fuzzy-match again or ask twice. For any other \`ok: false\` result, explain the error and offer its \`availableWorkProducts\`; never claim generation started.
For an independently selected SDD Optional Work Product in the **To Generate** state, call \`list_ui_elements()\` and pass its exact scoped card ID as \`generate_work_product({ elementId })\`. Do not reconstruct the ID or generate it against the current Required Work Products scope.
This back-to-Feature-Studio rule overrides the generic requirement to call \`list_ui_elements()\` first.
${
    uiBundle
        ? `\n\n# HOST UI ELEMENTS

## IMPORTANT: You HAVE These UI Tools Available


The following tools are registered and available for you to call RIGHT NOW:

- \`list_ui_elements()\` — Lists available UI buttons/navigation/tabs (NO PARAMETERS - just call it)
- \`activate_ui_element({elementId, rationale})\` — Clicks a UI element by ID

These tools ARE available in this session. For UI navigation without a dedicated navigation tool, call list_ui_elements() first.
Exception: if the user asks to go back/return from editor to Feature Studio, call \`navigate_to_feature_studio()\` directly (no UI lookup needed).
Exception: use the dedicated \`navigate_to_sdd_product_hierarchy\`, \`navigate_to_sdd_scope_picker\`, and \`navigate_to_sdd_progress_report\` tools directly for their FDE pages; do not call \`list_ui_elements()\` first.
Exception: named feature and user-story selections use \`navigate_to_feature\` or \`navigate_to_user_story\` directly; after success, end the turn without calling \`list_ui_elements()\`.

## MANDATORY: When Asked About UI Elements

If user asks "What UI elements are available?" or "What can I click?" or similar:
→ IMMEDIATELY call \`list_ui_elements()\`
→ Show the results
→ Do NOT describe what you can do, just call the tool

## When to Use: UI Elements vs Products

**Product name mentioned** (verified by calling product tools):
- "Open <product name>" → \`navigate_to_project({ projectName: "<product name>" })\`

**UI element label mentioned** (from list_ui_elements, excluding features and stories):
- "Click Build a new Product" → \`activate_ui_element\`
- "Go to Products page" → \`activate_ui_element\`

When in doubt: if it matches a product from list_projects, use product tools. If it matches a UI element from list_ui_elements, use UI tools.
For opening existing features/user stories, prefer \`navigate_to_feature\` / \`navigate_to_user_story\` over generic UI-element clicks.

## CRITICAL: Work Product Generation/Opening

When the user asks to generate a work product (PRD, RICE Analysis, User Flow, etc.):

1. **DO NOT ask for parameters or inputs** - the application will handle that
2. **DO NOT try to generate content yourself** - use host tools to trigger generation
3. **PREFER** \`generate_work_product({ workProductName, phase? })\` for generation requests
4. If \`generate_work_product\` fails, report its error or confirmation prompt. Do not substitute \`activate_ui_element()\`: activating a Build control does not prove that a generation job was submitted.
5. Never claim generation started unless \`generate_work_product\` returns both \`ok: true\` and a non-empty \`jobId\`.

The host-level generate tool is more reliable on first turn than DOM-card lookup because it can queue and retry while work products are loading.

When presenting currently available work products from \`list_ui_elements()\`, list them using status labels in this shape:
- \`<Work Product Name> (Ready)\`
- \`<Work Product Name> (To Generate)\`
- \`<Work Product Name> (Generating)\`
Do NOT prepend action verbs like "Open" or "Generate" in that list.

## Disambiguating Work Products by Context

**IMPORTANT**: Some work products exist at multiple levels (e.g., PRD at both Product and Feature levels). The WP cards visible in \`list_ui_elements()\` are **context-aware** - they only show what's relevant to the current view.

The SDD workspace can display more than one hierarchy scope at once (for example, current feature Required Work Products alongside independently selected Optional Work Products). For any request about "this" or the "current" product, feature, or user story, call \`get_current_hierarchy()\` and keep only work-product IDs matching that returned scope. Never attribute a card from another scope panel to the current hierarchy selection.

**Element ID Pattern**: \`wp-{context}-{phase}-{wpId}\`
- \`wp-product-discovery-prd\` — Product-level PRD
- \`wp-feature-123-discovery-prd\` — Feature #123's PRD
- \`wp-userstory-456-code-unit-tests\` — User Story #456's unit tests

**How to choose the right one**:

1. **Check what's currently visible** - \`list_ui_elements()\` only returns cards for the current context
   - If user is viewing Product level → only product-level WPs appear
   - If user is viewing Feature #123 → only feature-123 WPs appear
   - If user is viewing User Story #456 → only userstory-456 WPs appear

2. **Use the context in the element ID to confirm**:
   - "Generate the PRD" when viewing Feature #123 → activate \`wp-feature-123-discovery-prd\` (NOT \`wp-product-discovery-prd\`)
   - "Generate the PRD" when viewing Product → activate \`wp-product-discovery-prd\`

3. **If the user is explicit about level**:
   - "Generate the **product-level** PRD" → activate \`wp-product-*-prd\`
   - "Generate the **feature** PRD" → activate \`wp-feature-*-prd\`
   - "Generate PRD for this user story" → activate \`wp-userstory-*-prd\`

4. **Default to current context** - If user just says "Generate the PRD" without specifying level, use whatever PRD card appears in the current \`list_ui_elements()\` result (the UI only shows contextually-relevant cards)

5. **Use English in tool calls** - every free-text tool argument, especially \`rationale\`, must be a short English sentence. Keep the exact \`elementId\` returned by \`list_ui_elements()\` unchanged.

6. **Keep skill names ASCII and canonical** - for \`Skill\` tool calls, \`name\` must be the exact built-in skill id (lowercase English letters, numbers, and hyphens only, like \`build-new-product\`). Never pass translated, localized, or non-ASCII text as the skill name.

**Examples**:

\`\`\`
Scenario: User viewing Feature #123, Discovery tab

list_ui_elements() returns:
- wp-feature-123-discovery-prd (Product Requirements Definition (To Generate))
- wp-feature-123-discovery-user-stories (User Stories (To Generate))
- wp-feature-123-discovery-acceptance-criteria (Acceptance Criteria (Ready))

User: "Generate the PRD"
→ Activate wp-feature-123-discovery-prd (Feature-level PRD)

User: "Generate the product-level PRD"
→ First check if wp-product-discovery-prd is in list
→ If not visible, tell user: "The product-level PRD isn't visible from this view. Would you like me to navigate to the product level first?"
\`\`\`

**Key Principle**: The UI Bridge only shows WP cards relevant to the current view. If a user asks for a WP that's not in \`list_ui_elements()\`, it means they need to navigate to a different level/tab first.`
        : ""
}`}
                            height="100%"
                            theme="light"
                            onUIEvent={handleUIEvent}
                            onError={err => {
                                console.error("[CodeNexusChat] error:", err);
                                batchGeneration.failPendingChat("The batch request could not be routed. Please try again.");
                            }}
                            cards={{
                                [KNOWLEDGE_INGESTION_CARD]: KnowledgeIngestionHostCard
                            }}
                            onFilesAdded={handleFilesAdded}
                            filesAccept={ACCEPTED_FILE_TYPES}
                            hooks={chatHooks}
                            slots={{ Input: ChatInputSlot }}
                        >
                            {/* DocumentBridge renders nothing; it subscribes to the
                        adapter inside ChatProvider so list_open_documents and
                        edit_resolutions ride the existing pushContext drain. */}
                            <DocumentBridge />
                            {/* ContextMeshBridge publishes analyzer-backed ambient
                            health and exposes the host adapter to Raj's tools. */}
                            <ContextMeshBridge />
                            {/* UIBridge renders nothing; it subscribes to the adapter
                        inside ChatProvider so UI element context is pushed to
                        the agent via [ui-context]. */}
                            {UIBridge && <UIBridge />}
                            {/* DocumentContextWatcher pushes a passive host_navigation
                        note when the active section changes; it does NOT fire
                        a chat turn. */}
                            <DocumentContextWatcher />
                            <EditGuardWatcher resolveSectionByDocId={resolveSectionByDocId} />
                            <EngagementAwarePageOrchestrator
                                pageId={orchestratorPageId}
                                engagementSequence={pageEngagementSequence}
                                pageContext={{
                                    productId: activeProduct?.projectId ?? undefined,
                                    productName: activeProduct?.projectName ?? undefined,
                                    contextMeshId: activeContextMeshId ?? undefined,
                                    userName: user?.firstName || user?.fullName || undefined,
                                    currentRoute: typeof window !== "undefined"
                                        ? routeWithSearch(window.location.pathname, window.location.search)
                                        : undefined,
                                    workProductName: activeWorkProduct?.workProductTitle ?? undefined,
                                    workProductType: activeWorkProduct?.workProductLevel ?? undefined,
                                    editorSurface: activeWorkProduct?.editorSurface ?? undefined,
                                    workProductContentType: activeWorkProduct?.workProductContentType ?? undefined,
                                    workProductPhase: activeWorkProduct?.workProductPhase ?? undefined,
                                    suppressPageWelcome: suppressFeatureStudioWelcome,
                                    editorMode: inferEditorModeFromWorkProductContext(
                                        activeWorkProduct?.editorSurface,
                                        activeWorkProduct?.workProductLevel,
                                        activeWorkProduct?.workProductContentType,
                                        activeWorkProduct?.workProductPhase
                                    )
                                }}
                                rules={PAGE_RULES}
                            />
                            <ActiveIntentWatcher activeIntentSkillRef={activeIntentSkillRef} />
                            <ViewContextPusher
                                currentView={currentView}
                                activeProduct={activeProduct}
                                activeWorkProduct={activeWorkProduct}
                                activeHierarchy={activeHierarchy}
                            />
                            <ExposeChatActions handleRef={chatActionsRef} />
                            <ExposeHostCards apiRef={hostCardsRef} registry={batchRegistry} />
                            <BatchGenerationBridge />
                        </CodeNexusChat>
                        </UIEventBusProvider>
                        </StagedEditChipsContext.Provider>
                    </IngestionBatchRegistryContext.Provider>
                </UserStateProvider>
            </MuiBox>

            {/* Pending-edits guard for agent-initiated section switches. */}
            <Dialog open={pendingSwitch !== null} onClose={cancelPendingSwitch} aria-labelledby="discard-pending-edits-title">
                <DialogTitle id="discard-pending-edits-title">Discard pending edits?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The agent has staged edits on the current section that you haven&apos;t accepted or rejected yet. Switching to{" "}
                        <strong>{pendingSwitch?.sectionTitle ?? ""}</strong> will discard them. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelPendingSwitch} color="inherit">
                        Stay here
                    </Button>
                    <Button onClick={confirmPendingSwitch} variant="contained" color="error" autoFocus>
                        Discard &amp; switch
                    </Button>
                </DialogActions>
            </Dialog>
        </MuiBox>
    );
};

export default AIChatPanel;
